// Taipei, "Mist round Taipei 101". A warm October night just after a shower, from the Six Giant Rocks on Xiangshan
// (Elephant Mountain), about 150 m above the city on the east edge of the basin. We stand on the lookout's wet boardwalk
// between the big sandstone boulders and look west-north-west (about 308 degrees): Taipei 101, 1.3 km off at 303
// degrees, nearer and taller than anything else, its eight flared segments lit pale blue and its crown and spire in the
// day's colour (violet: a Sunday); the towers of Xinyi round its foot, Taipei Nan Shan Plaza just right of it and a
// little nearer, The Sky Taipei and the other Xinyi towers stepping away to the right, the Taipei Dome's silver oval
// behind them; the whole basin of lights beyond, avenues of scooters and cars running off towards the Tamsui river,
// the Wenhu line's viaduct in the middle distance, Songshan airport's runway far right, where planes come in from the
// west to land (runway 10, the northeast-monsoon direction). On the horizon Guanyin Mountain stands almost behind the
// tower, the Linkou tableland runs off to the left and the foothills of Yangmingshan climb at the right. Far left, the
// Shin Kong Life Tower by Taipei Main Station. A residential tower at the foot of the mountain carries the rooftop
// billboard. Below the rail the forest of the slope; around us the boulders, tree ferns, trail lamps and stone steps.
// Not in view from here: the night markets, Longshan Temple, the Grand Hotel (all behind the city to the west and north).
// render(t, { crowd }) thins the people on the lookout; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds; everything else runs all loop long):
//   event                                 time               size
//   a plane lands on runway 10            14-66, 134-186     medium (behind 101 at ~38 / ~158, touchdown 58 / 178)
//   light drizzle swells and eases        28-50              small
//   hikers climb the steps, look, leave   24-140             medium (pair A); pair B 142-226
//   the Wenhu line train crosses          every 40 s         small
//   the dog walker crosses the boardwalk  62-92, 172-200     medium
//   a mist bank swallows 101's waist      70-128             big (gathers 70-88, covers 88-108, frees 108-128)
//   a plane takes off to the east         92-108             medium
//   the jogger runs by                    110-124, 220-232   small
//   the façade show runs up the tower     150-182            big (crown sparkles 150, colour climbs 156-164,
//                                                                 rainbow and a heart 164-174, fades 174-182)
//   a shower: umbrellas open and close    196-232            big
//   all loop: scooter and car lights stream along the avenues, aviation lamps blink, windows switch on and off,
//   the couple on the boulder pose for photos every 12 s, the photographer exposes and checks his screen, the
//   bubble-tea sipper sips every 20 s, the cat grooms and flicks its tail, moths circle the trail lamps, three cloud
//   layers drift, haze breathes over the basin, the wet boardwalk shimmers under the lamps and the tower.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The view: a camera on the rocks ================= */
// Ground positions are metres east (e) and north (n) of the lookout; heights are metres above sea level. The eye is at
// 150 m, the basin floor at about 10 m. A pinhole camera facing 308.2 degrees, 482 px focal length (a 53-degree view).
const EYE = 152, FOC = 482, EYE_H = 150;
const B0 = 308.2 * Math.PI / 180, FX = Math.sin(B0), FY = Math.cos(B0), RX = Math.cos(B0), RY = -Math.sin(B0);
const LAT0 = 25.0273, LON0 = 121.5757, M_LAT = 111200, M_LON = 100880;
/** screen position of a point e m east, n m north, h m above sea level (z: its depth in metres) */
function proj(e, n, h = 10) {
  const z = e * FX + n * FY, u = e * RX + n * RY;
  return { x: 240 + FOC * u / z, y: EYE + FOC * (EYE_H - h) / z, z };
}
const geo = (lat, lon, h = 10) => proj((lon - LON0) * M_LON, (lat - LAT0) * M_LAT, h);
const pxPerM = z => FOC / z;

/* ================= Layout ================= */
// Taipei 101, 1281 m off: 0.376 px a metre. Foot (10 m) at row 205, the 27th floor (123 m) at 158, the 91st (392 m) at
// 58, the roof (449 m) at 36, the spire's tip (508 m) at 14. Seen across its south-east corner (the east face takes 0.61
// of the width, on the right), so it shows about 1.3 times its face width: 24 px at a segment's foot, 28 at its lip.
const T = { cx: 196, tip: 14, roof: 36, crown: 58, mods: 158, foot: 205, modW0: 24, modW1: 28 };
const MODS = [];                                    // the eight segments, bottom first: { y0 (top row), y1 (bottom row) }
{ let y = T.mods; [13, 12, 13, 12, 13, 12, 13, 12].forEach(h => { MODS.push({ y0: y - h, y1: y - 1 }); y -= h; }); }
const NS = { cx: 269, top: 98, w: 20 };             // Taipei Nan Shan Plaza (272 m, 1186 m off)
const SKY_T = { cx: 311, top: 106, w: 13 };         // The Sky Taipei (280 m, 1393 m off)
const RES = { x0: 106, x1: 158, roof: 150 };        // the residential tower at the mountain's foot, about 450 m off
const BB = { x: 109, y: 125, w: 46, h: 18 };        // its rooftop billboard's face (the city's billboard)
const CANOPY = 192;                                 // the forest of the slope rises to about this row
const RAIL = 190, DECK = 201;                       // the lookout's rail (top) and the far edge of the boardwalk
const MOON = { x: 404, y: 44 };
const RUNWAY = { x0: 329, y0: 163, x1: 480, y1: 167 };   // Songshan's runway 10/28, its west end the far one
const ACCENT = '#e040fb';
const VIOLET = ['#3a1060', '#5a1a8e', '#8a34c8', '#b55cf0', '#d890ff', '#f2d0ff'];   // the crown and spire (Sunday)

/* ================= Timeline ================= */
const LAND = [{ t0: 14, td: 58, t1: 66 }, { t0: 134, td: 178, t1: 186 }];   // approach, touchdown, end of the rollout
const DEPART = { t0: 92, lift: 100, t1: 108 };
const MIST = { a: 70, full: 88, clear: 108, b: 128 };
const SHOW = { a: 150, climb: 156, top: 164, hold: 174, b: 182 };
const SHOWER = { a: 196, b: 232 }, UMB = [204, 224];
const LIGHT_RAIN = [28, 50];
const DOGW = [{ t0: 62, t1: 92, dir: 1 }, { t0: 172, t1: 200, dir: -1 }];
const JOG = [{ t0: 110, t1: 124, dir: -1 }, { t0: 220, t1: 232, dir: 1 }];
const HIKE = [{ t0: 24, at: 44, go: 118, t1: 140, x: 262, seed: 1 }, { t0: 142, at: 160, go: 206, t1: 226, x: 352, seed: 2 }];
const TRAINS = [0, 1, 2, 3, 4, 5].map(i => ({ t0: 6 + i * 40, dir: i & 1 ? -1 : 1 }));   // 22 s along the viaduct

/* ================= The crowd ================= */
// The share of the people the scene shows by itself: about half of a cast of 19. render(t, { crowd }) overrides it.
// Rank 0 (the couple on the boulder, the bubble-tea sipper, the photographer) stays while anybody does; crowd 0 draws
// nobody and takes the tripod, the cups, the phones, the dog and the umbrellas along. The cat stays.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { couple: 0, sipper: 0, photo: 0, hikeA: 0.12, friends: 0.3, hikeB: 0.42, dog: 0.48, sitters: 0.56, selfie: 0.64,
  family: 0.74, jog: 0.84, lone: 0.92 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    has(i, j) { return i >= 0 && j >= 0 && i < w && j < h && d[(j * w + i) * 4 + 3] > 0; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
const inWin = (t, a, b) => t >= a && t < b;
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}

/* ================= The sky: a damp autumn night, the basin's light under the clouds ================= */
const SKY = ['#06071a', '#090b20', '#0c0f26', '#10142d', '#141934', '#1a1d3b', '#202241', '#282747', '#312c4b', '#3b324e',
  '#473851', '#543e53', '#624554', '#704c55', '#7e5456'];
function renderSky() {
  const p = pix(W, EYE + 8);
  for (let y = 0; y < EYE + 8; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(y / (EYE + 8), 1.5) * 0.66;
    v += 0.3 * Math.exp(-(((x - 220) / 230) ** 2)) * smooth(60, EYE + 4, y);            // the basin's glow on the haze
    v += 0.08 * Math.exp(-(((x - T.cx) / 40) ** 2)) * smooth(10, 120, y);                // the tower's light in the damp air
    v += 0.1 * Math.max(0, 1 - Math.hypot((x - MOON.x) / 110, (y - MOON.y) / 60));     // moonlight through thin cloud
    v += (vnoise(x / 60, y / 9, 3) - 0.5) * 0.05;
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
function renderMoon() {                             // a waxing gibbous, a little hazy
  const r = 5, s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, d = Math.hypot(dx, dy);
    if (d > r + 0.3 || Math.hypot(dx + 3.4, dy) < r - 0.6) continue;                    // the shadowed limb on the left
    let col = dx + dy < -2 ? '#f6efe0' : '#dcd4cc';
    if (d > r - 1) col = '#b8b0b8';
    if (Math.hypot(dx - 1, dy + 1) < 1.2) col = '#c4bcc0';
    p.set(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(77);
  while (STARS.length < 22) {
    const x = Math.floor(r() * W), y = 4 + Math.floor(r() * 70);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22 || Math.abs(x - T.cx) < 16) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.2 + r() * 0.45 });
  }
}
/* broken cloud after the shower, tileable (period W): dark on top, lit from below by the basin's orange light */
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 40 + r() * 80, base = h * (0.45 + r() * 0.35);
    const np = 5 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 9 + r() * 18, ry = 3 + r() * 5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.15 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col, def.a);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
const CLOUD_DEFS = [
  { seed: 11, y: -6, h: 50, banks: 6, k: 1, a: 255, pal: { top: '#1e2038', body: '#171a30', core: '#14162a', bot: '#2c2840', bot2: '#221f36' } },
  { seed: 29, y: 30, h: 50, banks: 5, k: 2, a: 240, pal: { top: '#2a2a46', body: '#21223c', core: '#1c1d34', bot: '#584258', bot2: '#3c3048' } },
  { seed: 43, y: 74, h: 44, banks: 5, k: 3, a: 225, pal: { top: '#3a3450', body: '#2e2a44', core: '#28243c', bot: '#7a5660', bot2: '#5a4454' } },
];

/* ================= The horizon: the Linkou tableland, Guanyin Mountain, the foothills of Yangmingshan ================= */
function hillTop(x) {
  let y = 151 - 1.2 * vnoise(x / 14, 1, 31);                                           // the tableland, far and flat
  const g = (x - 240) / 70;                                                            // Guanyin Mountain: a broad cone with
  if (Math.abs(g) < 1) y = Math.min(y, 151 - 10 * Math.pow(1 - g * g, 1.6) - 2.2 * Math.exp(-(((x - 214) / 9) ** 2)));   // a shoulder
  const m = smooth(338, 480, x);                                                       // Yangmingshan's slopes, rising right
  if (m > 0) y = Math.min(y, 151 - m * 19 - 2.5 * vnoise(x / 9, 2, 32) * m - 3 * Math.exp(-(((x - 420) / 14) ** 2)));
  return Math.round(y);
}
/* ================= The basin of lights beyond about 4 km ================= */
const FAR_BOT = 175;                                // the far texture is drawn down to here (nearer buildings cover it)
const TWINKLE = [];                                 // far lights that twinkle
const LIGHT_COLS = ['#ffcf8a', '#ffb868', '#fff0d0', '#ffe2a8', '#d8e4ff', '#ff9a58', '#bff0e0', '#ffd8e8'];
function paintFar(p, r) {
  for (let x = 0; x < W; x++) {
    const top = hillTop(x);
    for (let y = top; y < EYE - 1; y++) {                                              // the hills: dark, hazier when farther
      const v = 0.35 + 0.25 * (y - top) / 12 + (vnoise(x / 6, y / 3, 33) - 0.5) * 0.12;
      p.set(x, y, y === top ? mixHex('#262640', '#4a3a52', 0.4 + 0.3 * smooth(338, 480, x)) : pick(['#151628', '#1a1a2e', '#211f34', '#2a2538'], v, x, y));
      if (y > top + 1 && hash(x, y, 34) < 0.008 * (x > 340 ? 1.5 : 1)) p.set(x, y, hash(x, y, 35) < 0.5 ? '#e8c890' : '#a8b8e0');   // roads on the slopes
    }
    for (let y = EYE - 1; y < FAR_BOT; y++) {                                          // the city: glowing haze, then lights
      const dist = 1 - smooth(EYE - 1, FAR_BOT, y), dist2 = vnoise(x / 22, y / 3, 36);
      const v = 0.28 + 0.4 * dist + (dist2 - 0.5) * 0.18;
      p.set(x, y, pick(['#110f22', '#171428', '#1e192e', '#2a2034', '#3a2a3a', '#4c343e'], v, x, y));
    }
  }
  for (let x = 0; x < W; x++) for (let y = FAR_BOT; y < 212; y++) p.set(x, y, pick(['#100e1e', '#161326', '#1c172c'], 0.4 + (vnoise(x / 9, y / 3, 39) - 0.5) * 0.4, x, y));   // under the nearer blocks
  // the lights: denser and finer towards the horizon, in districts, dimmer under the haze
  for (let k = 0; k < 4300; k++) {
    const x = Math.floor(r() * W), u = r(), y = Math.round(EYE - 1 + Math.pow(u, 1.5) * (FAR_BOT - EYE + 1));
    if (y < hillTop(x) + 2 && y < EYE) continue;
    const dist = 1 - (y - EYE + 1) / (FAR_BOT - EYE + 1), dens = 0.35 + 0.65 * vnoise(x / 18, y / 4, 37);
    if (r() > dens) continue;
    const col = LIGHT_COLS[Math.floor(r() * r() * LIGHT_COLS.length)];
    p.set(x, y, mixHex(col, '#4a3444', dist > 0.7 ? 0.55 : 0.25 * dist));
    if (r() < 0.05 && TWINKLE.length < 260) TWINKLE.push({ x, y, col, k: 3 + Math.floor(r() * 9), ph: r() });
  }
  // the Tamsui river: a thin dark band rising to Guandu, its bridges strung with lamps
  for (let x = 30; x < 330; x++) {
    const y = Math.round(lerp(162.4, 158.2, (x - 30) / 300));
    p.set(x, y, hash(x, 4, 38) < 0.18 ? '#c8a070' : '#0c0c1c');
  }
  for (const bx of [58, 96, 142, 188, 236, 284]) for (let k = -3; k <= 3; k++) {
    const y = Math.round(lerp(162.4, 158.2, (bx - 30) / 300));
    p.set(bx + k, y + (k > 0 ? 1 : 0), k & 1 ? '#ffd890' : '#fff0c8');
  }
  // the Shin Kong Life Tower by Taipei Main Station, 6 km off
  for (let y = 144; y < 160; y++) for (let x = 68; x <= 70; x++) p.set(x, y, x === 68 ? '#3a3448' : '#24202e');
  for (let y = 147; y < 160; y += 2) p.set(69, y, hash(y, 5, 39) < 0.5 ? '#e8c890' : '#2e2836');
}

/* ================= The middle distance: the blocks of Daan, Songshan and Xinyi, the avenues, the viaduct ================= */
// Buildings stand on a grid of real ground positions and are projected, far to near, so nearer ones hide farther ones.
// The avenues and the Wenhu line's viaduct are drawn first; where a building covers them their traffic doesn't show.
const ROADS = [                                     // E-W avenues (n fixed) and N-S roads (e fixed): metres
  { name: 'zhongxiao', n: 1579, e0: -900, e1: -8000 }, { name: 'renai', n: 1134, e0: -1100, e1: -7000 },
  { name: 'civic', n: 1913, e0: -800, e1: -8500, h: 28 }, { name: 'nanjing', n: 2750, e0: -1500, e1: -9000 },
  { name: 'minsheng', n: 3410, e0: -1800, e1: -9500 },
  { name: 'dunhua', e: -2690, n0: 700, n1: 4400 }, { name: 'guangfu', e: -1830, n0: 900, n1: 3400 },
  { name: 'jianguo', e: -3900, n0: 600, n1: 5400, h: 24 },
];
const VIADUCT = { e: -3230, n0: 700, n1: 3850, h: 25 };   // the Wenhu line on Fuxing Road
const WINS = [];                                    // windows that switch on and off: { x, y, w, h, c1, k, ph, duty }
const AVIATION = [];                                // red obstruction lamps: { x, y, ph }
function sampleLine(pts) {                          // screen samples about a pixel apart along a projected line
  const out = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], n = Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)));
    for (let k = 0; k < n; k++) out.push({ x: Math.round(lerp(a.x, b.x, k / n)), y: Math.round(lerp(a.y, b.y, k / n)), vis: false });
  }
  return out;
}
function roadPts(R0) {
  const pts = [];
  for (let s = 0; s <= 24; s++) {
    const f = s / 24, q = R0.n !== undefined ? proj(lerp(R0.e0, R0.e1, f), R0.n, R0.h || 10) : proj(R0.e, lerp(R0.n0, R0.n1, f), R0.h || 10);
    pts.push(q);
  }
  return pts;
}
const ROAD_S = [], VIA_S = [];
const BLD = [];                                     // the generated blocks: { x0, x1, top, base, z, col, lit }
function genBuildings(r) {
  for (let n = -400; n < 6200; n += 95) for (let e = -900; e > -7600; e -= 95) {
    const jn = n + (r() - 0.5) * 50, je = e + (r() - 0.5) * 50;
    const q = proj(je, jn, 10);
    if (q.z < 1350 || q.z > 4800 || q.x < -30 || q.x > W + 30) continue;
    const lat = LAT0 + jn / M_LAT, lon = LON0 + je / M_LON;
    const xinyi = lon > 121.5605 && lon < 121.570 && lat > 25.031 && lat < 25.043;
    if (r() < 0.22) continue;                                                          // streets, small parks, low roofs
    let h = 18 + r() * 34 + (r() < 0.16 ? 30 + r() * 60 : 0);
    if (xinyi) h = 35 + r() * 55;
    const fp = 30 + r() * 40, w = Math.max(2, Math.round(fp * FOC / q.z)), top = Math.round(proj(je, jn, 10 + h).y);
    BLD.push({ x0: Math.round(q.x - w / 2), x1: Math.round(q.x - w / 2) + w - 1, top, base: Math.round(q.y), z: q.z,
      col: ['#3a3446', '#2e3040', '#40384a', '#34343c', '#463c44', '#2c2c3c'][Math.floor(r() * 6)], lit: 0.25 + r() * 0.35,
      sign: r() < 0.06 && w >= 4, h });
  }
  BLD.sort((a, b) => b.z - a.z);
}
function paintMid(p, r, mask) {
  // the avenues: a dim line of street lamps; the elevated ones a shade lighter
  for (const R0 of ROADS) {
    const S = sampleLine(roadPts(R0));
    for (const s of S) { p.set(s.x, s.y, hash(s.x, s.y, 41) < 0.3 ? '#ffc070' : '#7a5240'); mask.set(s.x, s.y, 1); }
    ROAD_S.push({ ...R0, S });
  }
  const V = sampleLine([proj(VIADUCT.e, VIADUCT.n0, VIADUCT.h), proj(VIADUCT.e, VIADUCT.n1, VIADUCT.h)]);
  for (const s of V) { p.set(s.x, s.y, '#8a8a98'); p.set(s.x, s.y + 1, '#2a2834'); mask.set(s.x, s.y, 2); }
  VIA_S.push(...V);
  // the Taipei Dome: a low silver oval beyond Xinyi, its roof lit white
  const dm = geo(25.0436, 121.5600, 10), dTop = geo(25.0436, 121.5600, 92).y, half = Math.round(250 * FOC / dm.z / 2);
  BLD.push({ dome: true, cx: Math.round(dm.x), half, top: Math.round(dTop), base: Math.round(dm.y), z: dm.z });
  BLD.sort((a, b) => b.z - a.z);
  const wrap = { set(i, j, col, a) { p.set(i, j, col, a); mask.set(i, j, 0); } };
  for (const b of BLD) {
    if (b.tower) { b.tower(wrap); continue; }
    if (b.dome) {
      for (let x = b.cx - b.half; x <= b.cx + b.half; x++) {
        const u = (x - b.cx) / b.half, rt = Math.round(b.top + (b.base - b.top) * 0.55 * (1 - Math.sqrt(Math.max(0, 1 - u * u))));
        for (let y = rt; y <= b.base; y++) {
          const k = (y - rt) / Math.max(1, b.base - rt), roof = y - rt < 3 + 2 * (1 - Math.abs(u));
          p.set(x, y, roof ? pick(['#5a6074', '#8a92a8', '#b4bccc', '#d8dce6'], 0.75 - Math.abs(u) * 0.5 - (y - rt) * 0.12, x, y) : pick(['#1e1c2a', '#2a2836', '#3a3846'], 0.4 + k * 0.3, x, y));
          mask.set(x, y, 0);
        }
        if (hash(x, 3, 42) < 0.3) p.set(x, b.base - 2, '#fff2d0');
      }
      continue;
    }
    for (let y = b.top; y <= b.base; y++) for (let x = b.x0; x <= b.x1; x++) {
      let col = mixHex(b.col, '#141222', 0.35 + 0.35 * (y - b.top) / Math.max(1, b.base - b.top));
      if (y === b.top) col = mixHex(b.col, '#a89098', 0.45);                           // the roofline catches the glow
      else if (x === b.x0) col = mixHex(b.col, '#0a0a16', 0.55);                       // a shadowed side
      p.set(x, y, col); mask.set(x, y, 0);
    }
    for (let y = b.top + 2; y < b.base - 1; y += 2) for (let x = b.x0 + 1; x < b.x1; x += 2) {
      const v = r();
      if (v < b.lit * 0.75) p.set(x, y, mixHex(LIGHT_COLS[Math.floor(r() * r() * 5)], b.col, clamp((b.z - 1400) / 5000, 0, 0.45)));
      else if (v < b.lit && b.x1 - b.x0 > 4 && WINS.length < 420) WINS.push({ x, y, w: 1, h: 1, c1: LIGHT_COLS[Math.floor(r() * 4)], k: 1 + Math.floor(r() * 4), ph: r(), duty: 0.3 + r() * 0.5 });
    }
    if (b.sign) {                                                                      // a rooftop name sign: red, green or white
      const sw = Math.min(b.x1 - b.x0 - 1, 3 + Math.floor(r() * 4)), sx = b.x0 + 1, col = ['#e8705a', '#7ad8a0', '#f0e8d8', '#e888c8', '#7ab8e8'][Math.floor(r() * 5)];
      for (let x = sx; x < sx + sw; x++) p.set(x, b.top - 1, col);
    }
    if (b.h > 95 && r() < 0.4) AVIATION.push({ x: Math.round((b.x0 + b.x1) / 2), y: b.top - 1, ph: r() });
  }
}

/* ================= Xinyi's other towers ================= */
// Glass towers lit cool white along their fins, warmer offices inside; each painted by its own function into the depth
// order of the blocks (BLD) or, nearer than 101, into the front layer.
const GLASS = ['#0e1222', '#161c30', '#202a42', '#2e3a56', '#42506e', '#5e6e8c', '#8494b0', '#b0bed4'];
function towerBody(p, cx, top, base, w, opt = {}) {
  const x0 = Math.round(cx - w / 2), x1 = x0 + w - 1, sideW = Math.max(2, Math.round(w * (opt.side ?? 0.3)));
  for (let y = top; y <= base; y++) for (let x = x0; x <= x1; x++) {
    const side = x >= x1 - sideW + 1, k = (y - top) / Math.max(1, base - top);
    let v = (opt.v ?? 0.42) - k * 0.22 + (side ? -0.12 : 0.04) + ((y & 1) ? -0.05 : 0) + (opt.fins && (x - x0) % opt.fins === 0 ? 0.18 : 0);
    if (x === x1 - sideW + 1) v += 0.16;                                               // the corner catches the light
    p.set(x, y, pick(opt.ramp || GLASS, v, x, y, 0.6));
  }
  return { x0, x1 };
}
function paintSkyTaipei(p) {                        // The Sky Taipei: a slim shaft in bamboo-like nodes, a lantern crown
  const { cx, top, w } = SKY_T, base = 200, { x0, x1 } = towerBody(p, cx, top + 8, base, w, { fins: 3, v: 0.5, side: 0.35 });
  for (let y = top + 18; y < base; y += 11) for (let x = x0; x <= x1; x++) p.set(x, y, x > x1 - 4 ? '#7a8aa6' : '#c4d0e2');   // the nodes
  for (let j = 0; j < 8; j++) {                                                         // the crown narrows into a lit lantern
    const hw = Math.round(lerp(2, w / 2 - 1, j / 7));
    for (let x = cx - hw; x <= cx + hw; x++) p.set(x, top + j, j < 2 ? '#fff4d8' : pick(['#8a94ac', '#c8d2e4', '#fff0d0'], 0.4 + (x < cx ? 0.35 : 0) - j * 0.03, x, j));
  }
  for (let y = top - 6; y < top; y++) p.set(cx, y, '#9aa4bc');                           // its mast
  AVIATION.push({ x: cx, y: top - 7, ph: 0.3 });
}
function paintNanShan(p) {                          // Taipei Nan Shan Plaza: vertical fins drawn up into a pointed crown
  const { cx, top, w } = NS, base = 209, crownH = 16, x0 = Math.round(cx - w / 2), x1 = x0 + w - 1;
  for (let y = top; y <= base; y++) for (let x = x0; x <= x1; x++) {
    const u = (x - x0) / (w - 1), roof = top + Math.round(crownH * Math.abs(u - 0.62) * 1.5);   // the ridge leans right
    if (y < roof) continue;
    const side = u > 0.66, k = (y - top) / (base - top), fin = (x - x0) % 2 === 0;
    let v = 0.5 - k * 0.28 + (side ? -0.14 : 0) + (fin ? 0.12 : -0.04) + ((y & 1) ? -0.04 : 0);
    if (y < roof + 2) v = 0.92;                                                         // the crown's lit edge
    else if (y < top + crownH + 4 && fin) v += 0.22;                                    // the fins glow up into the crown
    p.set(x, y, pick(GLASS, v, x, y, 0.6));
  }
  for (let y = top + crownH + 8; y < base - 4; y += 3) for (let x = x0 + 1; x < x1; x += 2) if (hash(x, y, 51) < 0.22) p.set(x, y, hash(x, y, 52) < 0.6 ? '#ffe2a8' : '#e8f0ff');
  AVIATION.push({ x: Math.round(x0 + 0.62 * (w - 1)), y: top - 1, ph: 0.65 });
}
function paintSlab(p, cx, top, base, w, opt) {     // a plainer office tower: glass, a lit crown band, offices
  const { x0, x1 } = towerBody(p, cx, top, base, w, opt);
  for (let x = x0; x <= x1; x++) { p.set(x, top, opt.crown || '#c8d4e6'); p.set(x, top + 1, mixHex(opt.crown || '#c8d4e6', '#202a42', 0.5)); }
  for (let y = top + 4; y < base - 2; y += 2) for (let x = x0 + 1; x < x1; x += 2) if (hash(x, y, 53 + cx) < 0.18) p.set(x, y, hash(x, y, 54) < 0.6 ? '#ffd890' : '#dfe8ff');
  if (opt.mast) { for (let y = top - opt.mast; y < top; y++) p.set(cx, y, '#8a92a8'); AVIATION.push({ x: cx, y: top - opt.mast - 1, ph: hash(cx, 5) }); }
}
const XINYI = [                                     // [lat, lon, height m, width m, painter]
  [25.0365, 121.5661, 280, 40, p => paintSkyTaipei(p)],
  [25.0386, 121.5651, 208, 42, (p, q, top, w) => paintSlab(p, q.x, top, 200, w, { fins: 4, v: 0.4, crown: '#9ad0ff', mast: 4 })],   // Farglory Financial Center
  [25.0389, 121.5672, 167, 40, (p, q, top, w) => paintSlab(p, q.x, top, 200, w, { v: 0.36, crown: '#ffe0a0' })],
  [25.0405, 121.5655, 125, 44, (p, q, top, w) => paintSlab(p, q.x, top, 200, w, { v: 0.34, crown: '#e8eef8', fins: 5 })],             // a hotel
];
const XFRONT = [                                    // nearer than 101: drawn after it
  [25.0344, 121.5669, 272, 50, p => paintNanShan(p)],
  [25.0357, 121.5682, 212, 36, (p, q, top, w) => paintSlab(p, q.x, top, 210, w, { fins: 3, v: 0.44, crown: '#bfe8ff', mast: 5 })],   // Cathay Landmark
];

/* ================= Taipei 101 ================= */
// Ratios drawn to (508 m to the spire's tip): the pedestal to the 27th floor 0.24 of the height (drawn 47 of 191 px),
// the eight segments 0.53 (100 px), the crown 0.11 (22 px), the spire 0.12 (22 px); height to base width 7.7 face on,
// 5.9 across this corner (drawn 191 : 32). Each segment flares out by about 7 degrees and steps back in at the next.
const BLUE = ['#08142a', '#0e2442', '#14365e', '#1e4e7e', '#2e6c9e', '#4a8ebe', '#72b2da', '#a2d4ee', '#d4f0fc'];
const RAINBOW = ['#ff4a4a', '#ff9a3a', '#ffe04a', '#5ae86a', '#46d8e0', '#4a7aff', '#7a5aff', '#c45aff'];
const rampOf = col => [mixHex(col, '#000000', 0.88), mixHex(col, '#000000', 0.74), mixHex(col, '#000000', 0.58), mixHex(col, '#000000', 0.4),
  mixHex(col, '#000000', 0.22), col, mixHex(col, '#ffffff', 0.3), mixHex(col, '#ffffff', 0.55), mixHex(col, '#ffffff', 0.8)];
const modWidth = (i, y) => { const m = MODS[i], f = (m.y1 - y) / (m.y1 - m.y0); return Math.round(lerp(T.modW0, T.modW1, f)); };
/** one segment, in a ramp: glass lit up towards its flared lip, the east face (right) brighter, floor lines */
function paintModule(p, i, ramp, ox = 0, oy = 0) {
  const m = MODS[i];
  for (let y = m.y0; y <= m.y1; y++) {
    const w = modWidth(i, y), x0 = T.cx - (w >> 1), xc = x0 + Math.round(w * 0.39), f = (m.y1 - y) / (m.y1 - m.y0);
    for (let x = x0; x < x0 + w; x++) {
      const east = x > xc;
      let v = 0.36 + 0.34 * f + (east ? 0.1 : -0.12) + ((y & 1) ? -0.07 : 0) - (east ? (x - xc) / w * 0.12 : (xc - x) / w * 0.1);
      if (x === xc || x === xc + 2) v += 0.22;                                          // the double-stepped corner
      if (x === x0 || x === x0 + w - 1) v -= 0.16;
      if (y === m.y0) v = east ? 0.96 : 0.8;                                            // the lip, floodlit
      else if (y === m.y0 + 1) v -= 0.2;                                                // its shadow
      p.set(x - ox, y - oy, pick(ramp, v, x, y, 0.5));
    }
    if (y === m.y1 - 1) { const rx = Math.round((xc + x0 + w) / 2);                 // a ruyi at the foot of the east face
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1]]) p.set(rx + dx - ox, y + dy - oy, ramp[6]); }
  }
}
function paintPedestal(p) {                         // the tapering pedestal and the two coins we see, one on each face
  for (let y = T.mods; y <= T.foot; y++) {
    const f = (y - T.mods) / (T.foot - T.mods), w = Math.round(lerp(25, 32, f)), x0 = T.cx - (w >> 1), xc = x0 + Math.round(w * 0.39);
    for (let x = x0; x < x0 + w; x++) {
      const east = x > xc;
      let v = 0.3 + 0.12 * f + (east ? 0.1 : -0.1) + ((y & 1) ? -0.06 : 0);
      if (x === xc || x === xc + 2) v += 0.2;
      p.set(x, y, pick(BLUE, v, x, y, 0.5));
    }
  }
  const coin = (cx, cy) => {
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
      if (dx * dx + dy * dy > 5) continue;
      p.set(cx + dx, cy + dy, dx === 0 && dy === 0 ? '#2a1e10' : dx + dy < 0 ? '#ffe2a0' : '#c89a50');
    }
  };
  coin(T.cx - 9, T.mods + 4); coin(T.cx + 5, T.mods + 4);
}
function paintCrown(p, ramp, ox = 0, oy = 0) {     // floors 91-101 and the stepped cap (the day's colour)
  for (let y = T.roof; y < T.crown; y++) {
    let w;
    if (y >= 52) w = Math.round(lerp(18, 21, (57 - y) / 5));                           // the 91st floor's short flared tier
    else if (y >= 41) w = 14;                                                          // the communication floors, fins
    else w = [6, 8, 8, 10, 12][y - T.roof];                                            // the stepped cap
    const x0 = T.cx - (w >> 1), xc = x0 + Math.round(w * 0.39);
    for (let x = x0; x < x0 + w; x++) {
      const east = x > xc;
      let v = 0.5 + (east ? 0.12 : -0.12) + (y >= 41 && y < 52 && (x - x0) % 2 === 1 ? 0.12 : 0) + ((y & 1) ? -0.05 : 0);
      if (y === 52 || y === 41) v = 0.9;
      if (x === xc) v += 0.15;
      p.set(x - ox, y - oy, pick(y >= 52 ? BLUE : ramp, v, x, y, 0.5));
    }
  }
}
function paintSpire(p) {
  for (let y = T.tip; y < T.roof; y++) {
    const w = y >= 30 ? 4 : y >= 24 ? 3 : y >= 18 ? 2 : 1, x0 = T.cx - (w >> 1);
    for (let x = x0; x < x0 + w; x++) p.set(x, y, pick(VIOLET, (y === 33 || y === 27 || y === 21 ? 0.95 : 0.62) + (x === x0 ? -0.2 : 0.05), x, y, 0.5));
  }
}
const WIN101 = [];                                  // offices still lit on a few floors
function renderTower() {
  const p = pix(W, T.foot + 1);
  paintPedestal(p);
  for (let i = 0; i < 8; i++) paintModule(p, i, BLUE);
  paintCrown(p, VIOLET);
  paintSpire(p);
  const r = rng(101);
  for (let k = 0; k < 40; k++) {
    const i = Math.floor(r() * 8), m = MODS[i], y = m.y0 + 2 + 2 * Math.floor(r() * 5), w = modWidth(i, y), x = T.cx - (w >> 1) + 1 + Math.floor(r() * (w - 2));
    if (y < m.y1) WIN101.push({ x, y, k: 1 + Math.floor(r() * 3), ph: r(), col: r() < 0.7 ? '#ffe6b0' : '#fff8e8' });
  }
  return p.done();
}
/** the show's segment canvases: MOD_SHOW[i][c], segment i lit in RAINBOW[c] (cropped to the segment's box) */
function renderShowMods() {
  return MODS.map((m, i) => RAINBOW.map(col => {
    const p = pix(T.modW1 + 2, m.y1 - m.y0 + 1);
    paintModule(p, i, rampOf(col), T.cx - ((T.modW1 + 2) >> 1), m.y0);
    return p.done();
  }));
}

/* ================= The residential tower at the mountain's foot, and its rooftop billboard ================= */
const RES_WINS = [];
function renderResidence() {
  const p = pix(W, H), r = rng(808), { x0, x1, roof } = RES, side = x1 - 9;
  for (let y = roof; y < 240; y++) for (let x = x0; x <= x1; x++) {
    const s = x > side, floor = (y - roof) % 3;
    let v = 0.34 + (s ? -0.14 : 0) + (floor === 0 ? 0.14 : 0) + (vnoise(x / 3, y / 4, 61) - 0.5) * 0.06 - (y - roof) * 0.0015;
    if (x === x0) v -= 0.12;
    if (x === side + 1) v += 0.1;
    p.set(x, y, pick(['#14121e', '#1c1a28', '#262232', '#302a3a', '#3e3646', '#504654'], v, x, y, 0.5));
  }
  for (let y = roof + 2; y < 236; y += 3) {                                             // windows behind iron grilles, AC units
    for (let x = x0 + 3; x < side - 3; x += 7) {
      const v = x === x0 + 10 && y === roof + 14 ? 0 : r();                            // (the tap spot's window stays lit)
      if (v < 0.42) RES_WINS.push({ x, y, w: 3, h: 2, c1: ['#ffd890', '#ffe8c0', '#f0b870', '#a8c8ff', '#fff4e0'][Math.floor(r() * r() * 5)], k: 1 + Math.floor(r() * 3), ph: r(), duty: v < 0.24 ? 1 : 0.35 + r() * 0.4 });
      else { p.set(x, y, '#0c0a14'); p.set(x + 1, y, '#100e18'); p.set(x + 2, y, '#0c0a14'); }
      if (r() < 0.5) { p.set(x + 4, y + 1, '#5a5866'); p.set(x + 5, y + 1, '#4a4856'); }
    }
    for (let x = side + 2; x < x1; x += 4) if (r() < 0.3) RES_WINS.push({ x, y, w: 2, h: 2, c1: '#e8b878', k: 2, ph: r(), duty: 0.5 });
  }
  for (let x = x0 - 1; x <= x1 + 1; x++) { p.set(x, roof - 1, '#4a4454'); p.set(x, roof, '#6a6070'); }   // the parapet
  for (const [tx, w] of [[x0 + 6, 6], [x0 + 15, 6], [x1 - 14, 5]]) {                     // stainless water tanks on the roof
    for (let y = roof - 6; y < roof - 1; y++) for (let x = tx; x < tx + w; x++) p.set(x, y, pick(['#3a3a48', '#6a6c7a', '#9a9caa'], 0.25 + (x - tx) / w * 0.6, x, y));
    for (let x = tx; x < tx + w; x++) p.set(x, roof - 7, '#8a8c9a');
  }
  for (let y = roof - 9; y < roof - 1; y++) for (let x = x1 - 7; x < x1 - 1; x++) p.set(x, y, y === roof - 9 ? '#5a5464' : '#2a2634');   // the stair housing
  return trim(p);
}
const STEEL = { d: '#0e0e18', m: '#1c1a28', h: '#34303e', l: '#5a5462' };
function renderBoardFrame() {                       // the steel frame on the roof, braced, two lamp arms over the face
  const p = pix(W, H), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  for (const lx of [BB.x + 5, BB.x + 22, BB.x + 39]) for (let y = B; y < RES.roof - 1; y++) { p.set(lx, y, STEEL.m); p.set(lx + 1, y, STEEL.d); }
  p.line(BB.x + 6, B + 1, BB.x + 22, RES.roof - 2, STEEL.d); p.line(BB.x + 23, B + 1, BB.x + 39, RES.roof - 2, STEEL.d);
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) {
    let col = STEEL.m;
    if (y === T0) col = STEEL.l; else if (y === T0 + 1 || x === L) col = STEEL.h; else if (y === B || x === Rr) col = STEEL.d;
    p.set(x, y, col);
  }
  for (const ax of [BB.x + 10, BB.x + 35]) { p.set(ax, T0 - 1, STEEL.h); p.set(ax, T0 - 2, STEEL.h); p.set(ax + 1, T0 - 3, '#d8d0b8'); p.set(ax, T0 - 3, STEEL.l); }
  return trim(p);
}
const BIG = {                                       // 5x7 letters for the shop's name
  M: ['10001', '11011', '10101', '10101', '10001', '10001', '10001'], O: ['01110', '10001', '10001', '10001', '10001', '10001', '01110'],
  N: ['10001', '11001', '10101', '10011', '10001', '10001', '10001'],
};
/** the own ad: MOON / BOBA TEA, a cup of milk tea with its dome lid, straw and pearls (pearl frame f) */
function renderAd(f) {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++)
    p.set(i, j, i === 0 || j === 0 || i === w - 1 || j === h - 1 ? '#3a2a4a' : pick(['#120c22', '#1a1230', '#24183c', '#2e1e48'], 0.75 - j / h * 0.6, i, j));
  for (let y = 5; y <= 15; y++) {                                                      // the cup, narrowing to its foot
    const inset = Math.floor((y - 5) / 5), x0 = 3 + inset, x1 = 11 - inset;
    for (let x = x0; x <= x1; x++) p.set(x, y, x === x0 || x === x1 ? '#f0e8f0' : y < 8 ? '#e8c8a0' : '#c8905a');
  }
  for (let x = 3; x <= 11; x++) p.set(x, 4, '#ffffff');
  for (let x = 4; x <= 10; x++) p.set(x, 3, x === 4 || x === 10 ? '#d8d0e0' : '#ffffff');   // the dome lid
  for (let x = 6; x <= 8; x++) p.set(x, 2, '#f0eaf4');
  for (let y = 0; y <= 6; y++) p.set(8 + (y < 2 ? 1 : 0), y, '#ff6ab8');                // the straw
  const pearls = [[[5, 13], [7, 14], [9, 13], [6, 11], [8, 12]], [[5, 14], [7, 13], [9, 14], [7, 11], [9, 11]], [[6, 13], [8, 14], [5, 12], [8, 11], [10, 13]]][f];
  for (const [x, y] of pearls) p.set(x, y, '#2a140c');
  let cx = 17;
  for (const ch of 'MOON') { BIG[ch].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === '1') p.set(cx + i, 2 + j, '#fff2dc'); }); cx += 6; }
  let gx = 13;
  for (const ch of 'BOBA TEA') {
    const g = { B: ['110', '101', '110', '101', '110'], O: ['010', '101', '101', '101', '010'], A: ['010', '101', '111', '101', '101'],
      T: ['111', '010', '010', '010', '010'], E: ['111', '100', '110', '100', '111'], ' ': ['000', '000', '000', '000', '000'] }[ch];
    g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.set(gx + i, 11 + j, '#ff8ae8'); });
    gx += 4;
  }
  return gradeAd(p.done(), { level: 0.86, tint: '#1a1430', mix: 0.12, wear: 0.2, seed: 7 });
}

/* ================= Mist: wisps at the tower's waist, haze over the basin ================= */
function makeFog(h, seed, cols, dens, profile) {     // tileable in x (period W): soft dithered cloud with a vertical profile
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const n = 0.55 * vnoise(x / 34, y / 7, seed, Math.round(W / 34)) + 0.3 * vnoise(x / 13, y / 4, seed + 1, Math.round(W / 13)) + 0.15 * vnoise(x / 5, y / 2, seed + 2, Math.round(W / 5));
    const v = n * profile(y / (h - 1)) - (1 - dens);
    if (v <= bayer(x, y) * 0.35) continue;
    p.set(x, y, pick(cols, clamp(v * 1.6, 0, 1), x, y), Math.round(clamp(v * 2.2, 0.25, 1) * 255));
  }
  return p.done();
}
const bell = u => Math.sin(Math.PI * clamp(u, 0, 1));

/* ================= The forest below the rail, the near blocks of Xinyi ================= */
function renderFront(r) {                           // low Xinyi blocks between 0.95 and 1.35 km, their tops over the trees
  const p = pix(W, H), list = [];
  for (let n = 300; n < 2200; n += 70) for (let e = -1500; e > -2600; e -= 70) {
    const q = proj(e + (r() - 0.5) * 40, n + (r() - 0.5) * 40, 10);
    if (q.z < 950 || q.z > 1350 || q.x < 150 || q.x > 470 || r() < 0.35) continue;
    if (Math.abs(q.x - T.cx) < 20) continue;                                             // 101's own plaza
    list.push({ q, h: 25 + r() * 45, fp: 30 + r() * 30, col: ['#3a3446', '#2e3040', '#40384a', '#463c44'][Math.floor(r() * 4)] });
  }
  list.sort((a, b) => b.q.z - a.q.z);
  for (const b of list) {
    const w = Math.round(b.fp * FOC / b.q.z), x0 = Math.round(b.q.x - w / 2), top = Math.round(EYE + FOC * (EYE_H - 10 - b.h) / b.q.z);
    for (let y = top; y < 200; y++) for (let x = x0; x < x0 + w; x++) p.set(x, y, y === top ? mixHex(b.col, '#b09aa0', 0.4) : x === x0 ? mixHex(b.col, '#0a0a16', 0.5) : mixHex(b.col, '#141222', 0.4 + 0.3 * (y - top) / 20));
    for (let y = top + 2; y < 199; y += 2) for (let x = x0 + 1; x < x0 + w - 1; x += 2) if (r() < 0.4) p.set(x, y, LIGHT_COLS[Math.floor(r() * r() * 6)]);
    if (r() < 0.25) for (let x = x0 + 1; x < Math.min(x0 + w - 1, x0 + 6); x++) p.set(x, top - 1, ['#ff5a4a', '#5aff9a', '#fff0d8', '#6ac8ff'][Math.floor(r() * 4)]);
  }
  return trim(p);
}
const canopyTop = x => Math.round(CANOPY - 3 * vnoise(x / 11, 1, 71) - 2 * vnoise(x / 4, 2, 72) + 2);
function renderCanopy() {
  const p = pix(W, H);
  for (let x = 0; x < W; x++) {
    const top = canopyTop(x);
    for (let y = top; y < 216; y++) {
      const n = vnoise(x / 5, y / 3, 73), crown = vnoise(x / 9, y / 5, 74);
      let v = 0.18 + 0.35 * n + 0.2 * crown - (y - top) * 0.012;
      if (y === top || (y === top + 1 && bayer(x, y) < 0.5)) v = 0.72;                  // the leaves on top catch the city's glow
      p.set(x, y, pick(['#070c0c', '#0b1412', '#111c18', '#18261e', '#223226', '#34402e', '#4a4a36'], v, x, y, 0.7));
    }
  }
  return p.done();
}

/* ================= The lookout: boulders, the boardwalk and its rail, the steps, trail lamps, tree ferns ================= */
// The Six Giant Rocks are rounded blocks of grey sandstone streaked by the rain; the boardwalk runs out between them.
const ROCK = ['#100e12', '#1a171c', '#252128', '#322c32', '#41393e', '#52484a', '#665a58', '#7e6e68', '#9a8878', '#b8a48e'];
const BOULDERS = [                                  // centre x, top row, half-width, squareness (higher: steeper sides), lit by
  { cx: 56, top: 164, hw: 84, p: 2.6, lamp: 146 }, { cx: 452, top: 172, hw: 64, p: 2.6, lamp: 392 },
  { cx: 16, top: 206, hw: 62, p: 2.2, lamp: 146 }, { cx: 66, top: 238, hw: 82, p: 2, lamp: 146 }, { cx: 474, top: 236, hw: 66, p: 2, lamp: 400 },
];
const boulderTop = (b, x) => {
  const u = Math.abs((x - b.cx) / b.hw);
  return u >= 1 ? 999 : b.top + (H + 24 - b.top) * (1 - Math.pow(1 - Math.pow(u, b.p), 1 / b.p)) + 1.5 * vnoise(x / 9, b.top, 75);
};
const STEPS = [];                                   // stone steps down to the right: { x0, x1, y }
for (let k = 0; k < 8; k++) STEPS.push({ x0: Math.round(330 + k * 7), x1: Math.round(388 + k * 9), y: 207 + Math.round(k * 7.6 + k * k * 0.25) });
const LAMPS = [{ x: 146, y: 181, foot: 203 }, { x: 392, y: 180, foot: 203 }, { x: 400, y: 219, foot: STEPS[4].y }];
const BENCH = { x0: 246, x1: 282, seat: 216 };      // a slatted bench on the boardwalk, facing the view
const VP = { x: 240, y: EYE };                      // the boards run towards the vanishing point
function renderLookout() {
  const p = pix(W, H), r = rng(4242);
  // the boardwalk: wet planks running out towards the view, butt joints staggered, the glow of the sky on the wet wood
  for (let y = DECK; y < H; y++) {
    const s = (y - VP.y) / (DECK - VP.y), k = (y - DECK) / (H - DECK);
    for (let x = 0; x < W; x++) {
      const xf = VP.x + (x - VP.x) / s, plank = Math.floor((xf - 100) / 6), within = (xf - 100) / 6 - plank;
      const joint = within < 0.16 / s * 1.2, butt = Math.abs(frac(((y - DECK) / (8 + k * 30)) + hash(plank, 3, 86)) - 0.5) < 0.012 + 0.012 * k && hash(plank, 5, 88) < 0.5;
      let v = 0.28 + 0.05 * hash(plank, Math.floor((y - DECK) / (8 + k * 30) + hash(plank, 3, 86)), 81) + 0.22 * (1 - k) * (1 - k) + (vnoise(x / 24, y / 3, 82) - 0.5) * 0.1;
      if (joint || butt) v = 0.04;
      p.set(x, y, pick(['#0a090c', '#141012', '#1e1716', '#28201c', '#342a24', '#443830', '#58483e'], v, x, y, 0.6));
    }
  }
  // the rail: posts and two wet pipes along the far edge
  for (let x = 150; x <= 384; x++) {
    p.set(x, RAIL, '#7a7a8a'); p.set(x, RAIL + 1, '#2c2a36'); p.set(x, RAIL + 6, '#5a5a6a'); p.set(x, RAIL + 7, '#24222c');
    if ((x - 150) % 26 === 0) for (let y = RAIL; y <= DECK + 1; y++) { p.set(x, y, '#2a2832'); p.set(x + 1, y, '#18161e'); }
  }
  // the boulders: rounded sandstone, rain streaks running down, a wet sheen along the tops, the trail lamps' warm light
  for (const b of BOULDERS) for (let x = Math.max(0, b.cx - b.hw); x < Math.min(W, b.cx + b.hw); x++) {
    const top = Math.round(boulderTop(b, x)), u = (x - b.cx) / b.hw;
    if (top >= H) continue;
    const streakX = hash(x, 1, b.cx) < 0.1 && !(hash(x - 1, 1, b.cx) < 0.1) ? 0.5 + hash(x, 2, b.cx) * 0.5 : 0;
    for (let y = top; y < H; y++) {
      const d = y - top, lampD = Math.hypot((x - b.lamp) / 70, (y - top - 14) / 46);
      let v = 0.36 + 0.3 * Math.exp(-lampD * lampD) - 0.22 * Math.abs(u) ** 3 - d * 0.0025 + (vnoise(x / 14, y / 9, 83) - 0.5) * 0.16 + (vnoise(x / 4, y / 3, 84) - 0.5) * 0.06;
      if (d < 1) v = 0.86; else if (d < 3) v = 0.7 + (bayer(x, y) < 0.5 ? 0.06 : 0);    // the wet top catches the sky
      else if (d < 6) v += 0.08;
      if (streakX && d > 3 && d < 8 + streakX * 30) v -= 0.09 * (1 - (d - 3) / (5 + streakX * 30));   // dark streaks where the rain runs off
      if (vnoise(x / 2.4, y / 1.8, 85) > 0.86 && d > 6) v -= 0.14;                     // a few weathering pits
      if (y > H - 30 && vnoise(x / 6, y / 3, 87) > 0.6) { p.set(x, y, pick(['#0c140e', '#142218', '#1c2e1e'], v, x, y)); continue; }   // moss at the foot
      p.set(x, y, pick(ROCK, v, x, y, 0.55));
    }
    if (Math.abs(u) < 0.95) for (let y = top; y < top + 1; y++) if (hash(x, 9, b.cx) < 0.5) p.set(x, y, '#c8b8a4');
  }
  // the steps: granite slabs, wet, a handrail on posts beside them
  for (const s of STEPS) {
    for (let x = s.x0; x <= s.x1; x++) {
      p.set(x, s.y, '#8a8076'); p.set(x, s.y + 1, '#5a5048');
      for (let y = s.y + 2; y < s.y + 7; y++) p.set(x, y, pick(['#1a1614', '#241e1a', '#2e2620'], 0.5 - (y - s.y) * 0.08, x, y));
    }
  }
  for (let k = 0; k < STEPS.length - 1; k++) { const a = STEPS[k], b = STEPS[k + 1]; p.line(a.x0 - 3, a.y - 9, b.x0 - 3, b.y - 9, '#6a6a78'); for (let y = a.y - 9; y < a.y; y++) p.set(a.x0 - 3, y, '#2a2832'); }
  // the trail lamps: dark posts with square lanterns
  for (const L of LAMPS) {
    for (let y = L.y + 4; y <= L.foot; y++) { p.set(L.x, y, '#16141c'); p.set(L.x - 1, y, y < L.y + 10 ? '#3a3846' : '#22202a'); }
    for (let y = L.y - 3; y <= L.y + 3; y++) for (let x = L.x - 2; x <= L.x + 2; x++) p.set(x, y, Math.abs(x - L.x) === 2 || Math.abs(y - L.y) === 3 ? '#14121a' : y < L.y ? '#fff4d0' : '#ffd890');
    for (let x = L.x - 3; x <= L.x + 3; x++) p.set(x, L.y - 4, '#14121a');
    p.set(L.x, L.y - 5, '#14121a');
  }
  // tree ferns at the edges: a shaggy trunk patterned with old frond scars, an umbrella of broad lacy fronds
  const fern = (fx, base, top, rad, seed) => {
    const fr = rng(seed);
    for (let y = top; y < base; y++) {
      const sx = fx + Math.round(Math.sin(y * 0.04 + seed) * 1.5), scar = ((y + (sx & 1) * 2) % 4) < 2;
      p.set(sx - 1, y, '#120e0c'); p.set(sx, y, scar ? '#2e2218' : '#1c1612'); p.set(sx + 1, y, scar ? '#3a2c1e' : '#241c16'); p.set(sx + 2, y, '#120e0c');
    }
    for (let j = 0; j < 16; j++) {
      const a = -Math.PI / 2 + (j / 15 - 0.5) * 3.9 + (fr() - 0.5) * 0.2, len = rad * (0.75 + fr() * 0.3), back = j % 2;
      for (let s = 0; s <= len; s++) {
        const u = s / len, x = fx + Math.cos(a) * s, y = top + Math.sin(a) * s * 0.7 + u * u * len * 0.6;
        const nx = -Math.sin(a), ny = Math.cos(a) * 0.7, lf = Math.round(5 * Math.sin(Math.PI * Math.min(1, u * 1.15)) + 0.5);
        for (let q = -lf; q <= lf; q++) {                                               // the leaflets either side of the stem
          const col = q === 0 ? '#2a3a20' : back ? (Math.abs(q) < 2 ? '#1a2a1a' : '#142016') : (Math.abs(q) < 2 ? '#2e4a2a' : q < 0 ? '#22381e' : '#1c301c');
          if (q !== 0 && (s + Math.abs(q)) % 2) continue;
          p.set(x + nx * q, y + ny * q + Math.abs(q) * 0.6, col);
        }
      }
    }
    for (const [dx, dy] of [[0, -1], [1, -1], [-1, 0], [0, 0], [1, 0], [2, 0], [0, 1]]) p.set(fx + dx, top + dy, dy < 0 ? '#6a5430' : '#4a3a22');   // the curled new fronds
  };
  fern(458, Math.round(boulderTop(BOULDERS[1], 458)) + 1, 138, 34, 92);           // growing from behind the boulder
  // a Taiwan acacia leaning in from the left edge: a dark trunk, its fine leaves overhanging the corner
  for (let y = 40; y < H; y++) { const tx = Math.round(6 + (H - y) * 0.035 + Math.sin(y * 0.06) * 1.2); for (let k = -2; k <= 2; k++) p.set(tx + k, y, k === 2 ? '#2a221c' : k === -2 ? '#0a0808' : '#16120f'); }
  for (const [bx, by, ex, ey] of [[12, 120, 40, 92], [11, 150, 30, 128], [9, 84, 46, 58], [8, 70, 24, 40], [13, 104, 62, 84]]) { p.line(bx, by, ex, ey, '#16120f'); p.line(bx + 1, by, ex + 1, ey, '#241c16'); }
  for (let y = 0; y < 150; y++) for (let x = 0; x < 96; x++) {                         // its crown: fine dense leaves in clumps
    const n = 0.6 * vnoise(x / 7, y / 5, 95) + 0.4 * vnoise(x / 2.5, y / 2, 96), cl = Math.hypot(x / 78, (y - 52) / 82);
    if (n - cl * 0.62 < 0.12 + bayer(x, y) * 0.12) continue;
    const lit = vnoise(x / 4, (y - 2) / 3, 97) > 0.62 && n > 0.55;                       // the city's glow on the outer leaves
    p.set(x, y, lit ? pick(['#24361e', '#34482a', '#4a5a34'], n, x, y) : pick(['#050908', '#09110c', '#0f1a12', '#162418'], n * 0.9, x, y));
  }
  // ground ferns along the boulders' feet and the boardwalk's ends
  for (let k = 0; k < 80; k++) {
    const x = Math.floor(r() * W), b = BOULDERS.find(B => Math.abs(x - B.cx) < B.hw * 0.98 && boulderTop(B, x) > 196 && boulderTop(B, x) < H);
    const y = b ? Math.round(boulderTop(b, x)) + 1 : (x < 150 || x > 386 ? 206 : 0);
    if (!y) continue;
    for (let j = 0; j < 5; j++) { const a = -Math.PI / 2 + (j - 2) * 0.5, len = 4 + r() * 5; p.line(x, y, x + Math.cos(a) * len, y + Math.sin(a) * len + len * 0.3, j & 1 ? '#1e3220' : '#284228'); }
  }
  // fallen acacia leaves on the wet boards
  for (let k = 0; k < 90; k++) {
    const x = Math.floor(r() * W), y = DECK + 2 + Math.floor(r() * (H - DECK - 2));
    if (BOULDERS.some(B => boulderTop(B, x) <= y)) continue;
    p.set(x, y, ['#5a4a22', '#6a5a2a', '#4a3a1a'][Math.floor(r() * 3)]); if (y > 230) p.set(x + 1, y, '#4a3a1a');
  }
  // leaves overhanging the top-left corner from a tree out of frame
  for (let k = 0; k < 900; k++) {
    const x = Math.floor(r() * 110), y = Math.floor(r() * 48);
    if (vnoise(x / 9, y / 6, 94) * (1 - x / 120) * (1 - y / 56) > 0.18 + r() * 0.1) p.set(x, y, pick(['#060a0a', '#0c1410', '#142018', '#1e2c20'], 0.3 + (y / 48) * 0.4 + r() * 0.2, x, y));
  }
  return p.done();
}
function renderBench() {                            // seen from behind: the backrest's slats, the seat, cast-iron ends
  const w = BENCH.x1 - BENCH.x0 + 1, p = pix(w, 16), K = '#121016', WOOD = '#4a3426', WOODL = '#6a4a32';
  for (let x = 1; x < w - 1; x++) { p.set(x, 0, '#8a7a6a'); p.set(x, 1, WOODL); p.set(x, 2, WOOD); p.set(x, 4, WOODL); p.set(x, 5, WOOD); p.set(x, 8, '#7a6a5a'); p.set(x, 9, WOOD); }
  for (const lx of [2, w - 3]) for (let y = 0; y < 16; y++) { p.set(lx, y, K); if (y > 9) p.set(lx + 1, y, '#26222c'); }
  return p.done();
}
/* ================= People ================= */
// One function draws everyone (after rome.js and stockholm.js): a look gives the proportions for a height and the
// colours dimmed for the night; person() puts it on its feet. A warm night: T-shirts, light jackets, caps, shorts,
// hikers with backpacks and headlamps. Shoulders and hair catch the trail lamps' warm light from behind us.
const SKINS = ['#f0c8a0', '#e0ac80', '#c48c62', '#9a6a46'], HAIRS = ['#141010', '#2a1c16', '#4a3020', '#6a4a30', '#8a6a4a', '#c8c0b8'];
const NIGHT = '#0c0e22', GLOWC = '#ffd8a0';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.32), top: m(top), topD: mixHex(top, NIGHT, dk + 0.28),
    topL: mixHex(top, GLOWC, 0.32), bot: m(bot), botL: o.shorts ? m(SKINS[skin]) : m(bot), shoe: m(o.shoe || '#1a161c'), long: o.long || 0,
    cap: o.cap ? m(o.cap) : null, pack: o.pack ? m(o.pack) : null, lamp: !!o.lamp, shorts: !!o.shorts };
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind (looking at the view), 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (phone, camera, cup), 3 out, 4 up and out (pointing),
 * 5 round a neighbour's back, 6 a V sign; o.lean: the head towards a neighbour; o.sit: seated; o.bend: bent forward
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1, kn = Math.round(legs * 0.45);
    if (!o.noShadow) { ctx.fillStyle = 'rgba(8,8,20,0.35)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); }
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 2, ly + kn, 2, legs - kn, P.botL); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, ly + kn, 2, legs - kn - 1, P.botL); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(x - 1, ly + kn, 2, legs - kn, P.botL); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      if (P.shorts) { R(tx, ly + kn, lw, legs - kn, P.botL); R(tx + bw - lw, ly + kn, lw, legs - kn, P.botL); }
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD); else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                             // the lamps' light on the shoulders
  if (P.pack && view !== 1) R(side ? (dir > 0 ? tx - 2 : tx + bw) : tx + 1, ty + 1, side ? 2 : bw - 2, th - 3, P.pack);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else if (st === 5) R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD);
    else { R(ax, hy, 1, ty - hy + 1, P.topD); R(ax - 1, hy - 2, 1, 2, P.skin); R(ax + 1, hy - 2, 1, 2, P.skin); }   // a V sign
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 2 - k, 1, 1, P.topD); R(x + dir * 4 + bend * dir, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.cap) { R(hx, hY - 1, hw, 2, P.cap); if (side) R(dir > 0 ? hx + hw : hx - 2, hY, 2, 1, P.cap); else if (view === 1) R(hx, hY + 1, hw, 1, P.cap); }
  if (P.lamp && view !== 0) {                                                          // a headlamp, lit
    const lx = side ? (dir > 0 ? hx + hw - 1 : hx) : hx + (hw >> 1);
    dot(lx, hY + 1, '#fffef0'); halo(HL.headlamp, lx, hY + 1, 0.8);
  }
  if (o.umb) {                                                                          // an umbrella over the head
    const ux = hx + (hw >> 1) + (o.umbX || 0), uy = hY - 4, rr = o.umbR || 6;
    for (let k = -rr; k <= rr; k++) { const yy = uy + Math.round((k * k) / (rr * 2)); R(ux + k, yy - 1, 1, 2, k < 0 ? o.umb : mixHex(o.umb, NIGHT, 0.35)); }
    R(ux, uy - 2, 1, 1, '#2a2a30');
    for (let y = uy; y < hY + 2; y++) R(ux, y, 1, 1, '#2a2a30');
  }
  return hY;
}
const PEOPLE = {
  she: look(19, 0, 0, '#f0e6dc', '#3a4a6a', { long: 4 }), he: look(21, 1, 0, '#2e5a8a', '#2a2a34', {}),
  sipper: look(20, 1, 1, '#c8504a', '#2e2c3a', { long: 3 }), photo: look(23, 2, 0, '#3a3a40', '#22222c', { cap: '#2a2a32', pack: '#4a3a2a' }),
  fA: look(21, 0, 0, '#e8c040', '#2a2a3a', {}), fB: look(22, 1, 2, '#4a8a6a', '#3a3a48', { cap: '#d8d0c0' }), fC: look(20, 0, 0, '#d86a8a', '#1e2030', { long: 3 }),
  parent: look(23, 3, 0, '#6a6a7a', '#2a2a34', { shorts: true }), child: look(11, 3, 0, '#f08a3a', '#3a4a8a', { dk: 0.22 }),
  sitA: look(19, 0, 1, '#7a5aa0', '#2a2a3a', { long: 3 }), sitB: look(20, 2, 0, '#e8e2d8', '#2a3048', {}),
  selfie: look(21, 0, 0, '#a8d0e8', '#e8e0d8', { long: 4, shorts: true }), lone: look(23, 1, 5, '#3a6a4a', '#2a2a34', { cap: '#c8423a', lamp: true }),
  hA: look(23, 1, 0, '#d8603a', '#2a2a34', { pack: '#2a4a6a', lamp: true }), hB: look(22, 0, 2, '#3a7ab8', '#383440', { pack: '#6a3a3a', lamp: true, long: 3 }),
  hC: look(24, 2, 0, '#5a7a3a', '#2a2a34', { pack: '#3a3a44', lamp: true, cap: '#2a3a5a' }), hD: look(22, 1, 3, '#e8d8b0', '#4a3a2a', { lamp: true, shorts: true }),
  dogw: look(26, 1, 0, '#4a4a5a', '#2a2a34', { dk: 0.34 }), jog: look(26, 2, 0, '#e8f040', '#1a1c28', { shorts: true, dk: 0.34, cap: '#1a1a24' }),
};
const UMBS = ['#3a6ab8', '#c8423a', '#e8c040', '#5a8a5a', '#2a2a34', '#d86aa8'];
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
const COUPLE = { she: 62, he: 152, heY: 212 }, SIPPER = { x: 258 }, PHOTO = { x: 225, tri: 214 }, FRIENDS = [290, 298, 307], FAMILY = { x: 352 };
const SITTERS = [430, 440], SELFIE = { x: 166 }, LONE = { x: 32 }, CAT = { x: 404 };
const DOG_ROWS = [
  ['.........ee.', '........eddn', 't.......ddd.', '.tlllllllll.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
  ['.........ee.', '........eddn', 't.......ddd.', '.tlllllllll.', '..dddddddd..', '...dd..dd...', '..d..d.d..d.'],
  ['............', '............', 't...........', '.tlllllllll.', '..ddddddddee', '..d.d..d.ddn', '..d.d..d.d..'],   // nose down at a puddle
];
const DOG_KEY = { d: '#8a6a40', l: '#b08a54', t: '#8a6a40', e: '#6a4a2a', n: '#1a1210' };
const CAT_ROWS = [                                   // a tabby stray, sitting, seen from the side: sitting / grooming
  ['.e.e...', '.eee...', '.ewe...', '.bbb...', 'bbbb...', 'bbbbb..', 'bbbbb..', 'bbbbbtt'],
  ['.......', '.e.e...', '.eee...', '.eeeb..', 'bbbb...', 'bbbbb..', 'bbbbb..', 'bbbbbtt'],
];
const CAT_KEY = { e: '#8a7058', w: '#d8e070', b: '#6a5440', t: '#6a5440' };
const PLANE_KEY = { k: '#1a1c28', g: '#4a4e5e', w: '#c8ccd8' };

/* ================= Drizzle, the city's own: fine drops at three depths, coming and going ================= */
const DRIZ = [
  { count: 170, nMin: 80, nMax: 100, len: 1, slope: 0.14, col: '#8c8ab4', a: 0.3, land: [150, 205], lit: false },
  { count: 120, nMin: 110, nMax: 130, len: 2, slope: 0.18, col: '#a6a4cc', a: 0.4, land: [194, 246], lit: true },
  { count: 56, nMin: 140, nMax: 160, len: 4, slope: 0.24, col: '#c8c8e8', a: 0.48, land: [H + 4, H + 4], lit: true },
];
DRIZ.forEach((L, li) => {
  const r = rng(500 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), th: r() });
});
/** how much of the drizzle falls: a thin veil, a swell at 28-50 and the shower at 196-232 */
const rainLevel = t => Math.min(1, 0.26 + 0.4 * env(t, LIGHT_RAIN[0], LIGHT_RAIN[1], 7) + 0.8 * env(t, SHOWER.a, SHOWER.b, 9));
const showerOn = t => env(t, SHOWER.a, SHOWER.b, 9);
const RAIN_LIGHTS = [
  ...LAMPS.map(L => ({ x: L.x, y: L.y + 8, rx: 26, ry: 34, col: '#ffd8a0' })),
  { x: T.cx, y: 96, rx: 22, ry: 86, col: '#a8d8f4' },
];
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + 23, y: BB.y + 9, rx: 30, ry: 16, col: '#f0b0e8' }];
function litAt(x, y, lights) {
  for (const l of lights) { const dx = (x - l.x) / l.rx, dy = (y - l.y) / l.ry; if (dx * dx + dy * dy < 1) return l.col; }
  return null;
}
function drawDrizzle(L, t, lights, full) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, k = Math.floor(q), u = q - k, cyc = ((k % d.n) + d.n) % d.n;
    if (!full) { let ts = (k - d.ph) * LOOP / d.n; ts = ((ts % LOOP) + LOOP) % LOOP; if (d.th > rainLevel(ts)) continue; }
    const yl = lerp(L.land[0], L.land[1], hash(d.id, cyc, 12)), fall = yl + 8, yh = yl - fall * (1 - u);
    if (yh < -2 || yh > H) continue;
    const xh = -20 + hash(d.id, cyc, 11) * (W + 40 + fall * L.slope) - (yl - yh) * L.slope;
    const lit = L.lit ? litAt(xh, yh, lights) : null;
    ctx.fillStyle = lit || L.col; ctx.globalAlpha = lit ? L.a * 1.6 : L.a;
    for (let j = 0; j < L.len; j++) ctx.fillRect(Math.round(xh - j * L.slope), Math.round(yh - j), 1, 1);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Taipei draws its own drizzle; the wet rocks and the mist stay in every
// pick. Far drops land on the city, mid ones on the forest and the boulders, near ones fall past the boardwalk.
const WEATHER = weatherPlans(['drizzle']);
const WX = { land: [[150, 205], [194, 246], [H + 4, H + 4]], lights: RAIN_LIGHTS };
const WX_BB = { ...WX, lights: RAIN_LIGHTS_BB };

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR_C, MID_C, TOWER_C, MOD_SHOW, FRONT_FAR, FRONT_NEAR, XFRONT_C, RES_C, FRAME_C, ADS, PITCH,
  CANOPY_C, LOOK_C, BENCH_C, FOG_K, FOG_W, FOG_B, FOG_L, VIG_C, DOG_SPR, CAT_SPR, SCRATCH;
const CARS = [];                                    // per road: { s: samples, cars: [{ k, ph, dir, col }] }
const GLINTS = [];                                  // wet glints on the boulders and the leaves
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 6, 18], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  for (const L of [WINS, AVIATION, TWINKLE, ROAD_S, VIA_S, BLD, WIN101, RES_WINS, CARS, GLINTS]) L.length = 0;
  HL = {
    moon: makeHalo(34, '#a8a0c0', 0.16, 1.8), moonCore: makeHalo(10, '#e0d8e8', 0.24, 1.5),
    tower: makeHalo(70, '#6ab4e8', 0.14, 1.6), towerCore: makeHalo(30, '#9ad4f4', 0.16, 1.4), crown: makeHalo(22, '#c070ff', 0.3, 1.5),
    spire: makeHalo(9, '#e0a8ff', 0.4, 1.3), beacon: makeHalo(4, '#ff4a40', 0.55, 1), fogGlow: makeHalo(56, '#8ac4ec', 0.32, 1.4),
    show: makeHalo(60, '#ffffff', 0.14, 1.5), nanshan: makeHalo(26, '#d8e8ff', 0.1, 1.5), sky: makeHalo(14, '#fff0d0', 0.18, 1.4),
    landing: makeHalo(7, '#fff8e0', 0.5, 1.2), tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), red: makeHalo(2, '#ff4040', 0.6, 1),
    lamp: makeHalo(46, '#ffc070', 0.22, 1.7), lampCore: makeHalo(12, '#fff0c8', 0.5, 1.3), headlamp: makeHalo(4, '#fffef0', 0.35, 1.1),
    spot: makeHalo(20, '#ffe8c0', 0.2, 1.4), bbGlow: makeHalo(30, '#ff8ae8', 0.08, 1.6), screen: makeHalo(3, '#9ad0ff', 0.45, 1.1),
    win: makeHalo(4, '#ffb860', 0.12, 1.3), train: makeHalo(3, '#fff4d0', 0.45, 1.1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  const r = rng(2026);
  { const p = pix(W, H); paintFar(p, r); FAR_C = p.done(); }
  {
    const p = pix(W, H), m = new Uint8Array(W * H), mask = { set(x, y, v) { x = Math.round(x); y = Math.round(y); if (x >= 0 && y >= 0 && x < W && y < H) m[y * W + x] = v; } };
    genBuildings(r);
    for (const [lat, lon, hgt, wm, paint] of XINYI) {                                   // the Xinyi towers beyond 101, in depth order
      const q = geo(lat, lon, 10), top = Math.round(geo(lat, lon, 10 + hgt).y), w = Math.round(wm * FOC / q.z);
      BLD.push({ z: q.z, tower: pp => paint(pp, q, top, w) });
    }
    paintMid(p, r, mask);
    for (const R0 of ROAD_S) for (const s of R0.S) s.vis = s.x >= 0 && s.x < W && s.y >= 0 && s.y < H && m[s.y * W + s.x] === 1;
    for (const s of VIA_S) s.vis = s.x >= 0 && s.x < W && m[s.y * W + s.x] === 2;
    MID_C = p.done();
  }
  for (const R0 of ROAD_S) {                                                           // the traffic: platoons from the lights
    const cars = [], ew = R0.n !== undefined, rr = rng(R0.S.length + 7);
    for (let pl = 0; pl < 9; pl++) {
      const dir = pl & 1 ? -1 : 1, base = rr(), k = (ew ? 3 : 2) + Math.floor(rr() * 3), size = 3 + Math.floor(rr() * 5);
      for (let c = 0; c < size; c++) cars.push({ k, ph: base + c * 0.006 + rr() * 0.004, dir, col: dir > 0 ? (rr() < 0.85 ? '#ff4a3a' : '#ffa040') : (rr() < 0.7 ? '#fff4dc' : '#ffe0a0') });
    }
    CARS.push({ s: R0.S, cars });
  }
  TOWER_C = renderTower(); MOD_SHOW = renderShowMods();
  FRONT_FAR = renderFront(rng(31), 1190, 1350); FRONT_NEAR = renderFront(rng(37), 950, 1190);
  { const p = pix(W, H); for (const [lat, lon, hgt, wm, paint] of XFRONT) { const q = geo(lat, lon, 10); paint(p, q, Math.round(geo(lat, lon, 10 + hgt).y), Math.round(wm * FOC / q.z)); } XFRONT_C = trim(p); }
  RES_C = renderResidence(); FRAME_C = renderBoardFrame(); ADS = [0, 1, 2].map(renderAd);
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  CANOPY_C = renderCanopy(); LOOK_C = renderLookout(); BENCH_C = renderBench();
  FOG_K = makeFog(56, 231, ['#56607e', '#6c7896', '#8692ae', '#a2aec6', '#bcc8dc'], 0.92, u => bell(u) ** 0.55);
  FOG_W = makeFog(48, 201, ['#4a5070', '#5e6886', '#76829e', '#929eb8'], 0.62, u => bell(u) ** 0.7);
  FOG_B = makeFog(30, 211, ['#3a3044', '#4a3c50', '#5c4a5a'], 0.55, u => bell(u));
  FOG_L = makeFog(22, 221, ['#2a2a3a', '#3a3848', '#4a4656'], 0.5, u => bell(u));
  VIG_C = renderVignette();
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  CAT_SPR = CAT_ROWS.map(rows => sprite(rows, CAT_KEY));
  const g = rng(616);
  SCRATCH = makeCanvas(W, 60);
  DECK_GL.length = 0;
  while (DECK_GL.length < 150) {
    const x = Math.floor(g() * W), y = DECK + 2 + Math.floor(Math.pow(g(), 0.8) * (H - DECK - 3));
    if (BOULDERS.some(B => boulderTop(B, x) <= y) || STEPS.some(S => x >= S.x0 - 3 && x <= S.x1 && y >= S.y - 1 && y <= S.y + 7)) continue;
    const lampNear = LAMPS.some(L => Math.abs(x - L.x) < 30 && y < L.foot + 40);
    DECK_GL.push({ x, y, k: 2 + Math.floor(g() * 6), ph: g(), c: lampNear ? '#ffe2b0' : Math.abs(x - T.cx) < 30 ? '#a8d4f0' : g() < 0.5 ? '#8a7a8a' : '#b0a090' });
  }
  while (GLINTS.length < 120) {                                                         // wet tops of the boulders, wet leaves
    const b = BOULDERS[Math.floor(g() * 2)], x = Math.round(b.cx + (g() - 0.5) * b.hw * 1.5), top = boulderTop(b, x);
    if (top > H - 2 || x < 0 || x >= W) continue;
    GLINTS.push({ x, y: Math.round(top) + Math.floor(g() * 4), k: 3 + Math.floor(g() * 6), ph: g(), c: g() < 0.5 ? '#ffe8c0' : '#c8d4f0' });
  }
  for (let k = 0; k < 50; k++) { const x = Math.floor(g() * W); GLINTS.push({ x, y: canopyTop(x) + Math.floor(g() * 3), k: 2 + Math.floor(g() * 5), ph: g(), c: '#8aa0a0' }); }
}

/* ================= Per-frame: sky, far city, planes ================= */
const winLit = (w, t) => w.duty >= 1 || frac(w.k * t / LOOP + w.ph) < w.duty;
const beacon = (t, ph) => smooth(0.35, 0.95, 0.5 + 0.5 * wave(t, 120, ph));       // the obstruction lamps pulse every 2 s
const mistLevel = t => smooth(MIST.a, MIST.full, t) * (1 - smooth(MIST.clear, MIST.b, t));
const showLevel = t => smooth(SHOW.a, SHOW.climb, t) * (1 - smooth(SHOW.hold + 4, SHOW.b, t));
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8ecff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.1 * wave(t, 3));
  ctx.globalAlpha = 0.85; ctx.drawImage(MOON_C, MOON.x - 5, MOON.y - 5); ctx.globalAlpha = 1;
  halo(HL.moonCore, MOON.x, MOON.y, 0.7);
  for (const L of CLOUDS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, -off, L.y); ctx.drawImage(L.c, W - off, L.y); }
}
function drawFog(c, y, t, k, a, rev = false) {
  const off = scroll(t, k, W), x = rev ? off : -off;
  ctx.globalAlpha = clamp(a, 0, 1);
  ctx.drawImage(c, x, y); ctx.drawImage(c, rev ? x - W : x + W, y);
  ctx.globalAlpha = 1;
}
function drawFar(t) {
  ctx.drawImage(FAR_C, 0, 0);
  for (const s of TWINKLE) if (wave(t, s.k * 10, s.ph) < -0.72) dot(s.x, s.y, '#2a2032');   // traffic and trees pass the far lights
  drawFog(FOG_B, 146, t, 1, 0.4 + 0.14 * wave(t, 4));                                 // the haze over the basin breathes
}
/** a jet at (x, y): mode 0 coming in nearly head-on (its landing lights), 1 rolling out side-on, 2 climbing away */
function drawJet(x, y, t, a, mode) {
  x = Math.round(x); y = Math.round(y);
  ctx.globalAlpha = a;
  if (mode === 0) { R(x - 2, y, 5, 1, '#2a2e3c'); R(x, y - 1, 1, 1, '#2a2e3c'); }
  else { R(x - 3, y, 7, 1, '#8a90a2'); R(x - 3, y - 2, 1, 2, '#8a90a2'); R(x - 1, y + 1, 3, 1, '#4a4e5e'); }
  ctx.globalAlpha = 1;
  if (mode !== 2) { const lx = mode ? x + 3 : x; ctx.globalAlpha = a; dot(lx, y, '#fffbe8'); ctx.globalAlpha = 1; halo(HL.landing, lx, y, a * (mode ? 0.55 : 0.95)); }
  if (frac(t / 1.2) < 0.18) { ctx.globalAlpha = a; dot(x, y - 1, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, x, y - 1, a); }
  if (frac(t / 1.2 + 0.5) < 0.06) { ctx.globalAlpha = a; dot(x - 3, y, '#ffffff'); dot(x + 3, y, '#ffffff'); ctx.globalAlpha = 1; }
}
const approach = s => proj(-4100 - s, 4656, 15 + 0.0524 * s);   // runway 10's glide path: 3 degrees, from the west
function drawPlanes(t) {
  for (const L of LAND) {
    if (t < L.t0 || t >= L.t1) continue;
    if (t < L.td) { const q = approach(13000 * (1 - (t - L.t0) / (L.td - L.t0))); drawJet(q.x, q.y, t, smooth(L.t0, L.t0 + 5, t), 0); }
    else { const v = (t - L.td) / (L.t1 - L.td), q = proj(-4100 + 1400 * (1 - (1 - v) * (1 - v)), 4656, 9); drawJet(q.x, q.y, t, 1 - smooth(L.t1 - 2.5, L.t1, t), 1); }
  }
  if (t >= DEPART.t0 && t < DEPART.t1) {
    let q;
    if (t < DEPART.lift) { const v = (t - DEPART.t0) / (DEPART.lift - DEPART.t0); q = proj(-4100 + 1300 * v * v, 4656, 9); }
    else { const w = (t - DEPART.lift) / (DEPART.t1 - DEPART.lift), e = -2800 + 1100 * w; q = proj(e, 4656, 9 + (e + 2800) * 0.12); }
    drawJet(q.x, q.y, t, smooth(DEPART.t0, DEPART.t0 + 2, t) * (1 - smooth(DEPART.t1 - 1.5, DEPART.t1, t)), t < DEPART.lift ? 1 : 2);
  }
}
/* ================= Per-frame: the middle distance and the traffic ================= */
function drawMid(t) {
  ctx.drawImage(MID_C, 0, 0);
  for (const w of WINS) if (winLit(w, t)) dot(w.x, w.y, w.c1);
  for (const road of CARS) {
    const n = road.s.length;
    for (const c of road.cars) {
      let i = Math.floor(frac(c.k * t / LOOP + c.ph) * n);
      if (c.dir < 0) i = n - 1 - i;
      const s = road.s[i];
      if (s.vis) dot(s.x, s.y, c.col);
    }
  }
  for (const tr of TRAINS) {                                                           // the Wenhu line's little train
    const dt = t - tr.t0;
    if (dt < 0 || dt >= 22) continue;
    const n = VIA_S.length, head = Math.round(lerp(-10, n + 10, dt / 22));
    for (let j = 0; j < 8; j++) {
      const s = VIA_S[tr.dir > 0 ? head - j : n - 1 - head + j];
      if (!s || !s.vis) continue;
      dot(s.x, s.y - 1, j === 0 ? '#fffbe8' : j & 1 ? '#fff0c0' : '#c8ccd8');
      if (j === 0) halo(HL.train, s.x, s.y - 1, 0.7);
    }
  }
  for (const a of AVIATION) { const b = beacon(t, a.ph); if (b > 0.05) { ctx.globalAlpha = b; dot(a.x, a.y, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, a.x, a.y, b); } }
}
/* ================= Per-frame: Taipei 101 ================= */
const HEART = ['.11.11.', '1111111', '1111111', '.11111.', '..111..', '...1...'];
function drawTower(t) {
  const mist = mistLevel(t), sh = showLevel(t);
  halo(HL.tower, T.cx, 100, 0.8 + 0.35 * mist);
  halo(HL.towerCore, T.cx, 118, 0.7 + 0.2 * mist);
  halo(HL.crown, T.cx, 45, 0.85 + 0.1 * wave(t, 6));
  halo(HL.spire, T.cx, 24, 0.75 + 0.1 * wave(t, 8, 0.3));
  ctx.drawImage(TOWER_C, 0, 0);
  ctx.globalAlpha = 0.4; R(T.cx - 7 + Math.floor(frac(t / 8) * 14), 42, 1, 9, '#f4d8ff'); ctx.globalAlpha = 1;   // light runs round the fins
  for (const w of WIN101) if (frac(w.k * t / LOOP + w.ph) < 0.62) dot(w.x, w.y, w.col);
  if (t >= SHOW.a && t < SHOW.b) {                                                     // the façade show
    for (let i = 0; i < 8; i++) {
      const a = smooth(SHOW.climb + i, SHOW.climb + i + 0.9, t) * (1 - smooth(SHOW.hold + 7 - i, SHOW.hold + 8 - i, t));
      if (a > 0.01) { ctx.globalAlpha = a; ctx.drawImage(MOD_SHOW[i][i], T.cx - ((T.modW1 + 2) >> 1), MODS[i].y0); }
    }
    ctx.globalAlpha = 1;
    const x0 = T.cx - 7;
    if (t < SHOW.top) {                                                                // the crown's LEDs sparkle first
      const st = step(t, 960), n = Math.round(14 * smooth(SHOW.a, SHOW.climb, t));
      for (let j = 0; j < n; j++) if (hash(j, st, 77) < 0.45) dot(x0 + Math.floor(hash(j, st, 78) * 14), 43 + Math.floor(hash(j, st, 79) * 6), '#ffffff');
    } else if (t < SHOW.hold + 3) {                                                    // then a heart, beating slowly
      ctx.globalAlpha = smooth(SHOW.top, SHOW.top + 1, t) * (1 - smooth(SHOW.hold + 1, SHOW.hold + 3, t)) * (0.75 + 0.25 * wave(t, 160));
      HEART.forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] === '1') R(x0 + 4 + i - 1, 43 + j, 1, 1, '#ff9ad8'); });
      ctx.globalAlpha = 1;
    }
    halo(HL.show, T.cx, 104, sh * 0.7);
  }
  const b = beacon(t, 0.1);
  ctx.globalAlpha = b; dot(T.cx, T.tip - 1, '#ff4a40'); ctx.globalAlpha = 1; halo(HL.beacon, T.cx, T.tip - 1, b);
}
function drawFront(t) {
  blit(FRONT_FAR);
  halo(HL.nanshan, NS.cx, NS.top + 26, 0.8);
  halo(HL.sky, SKY_T.cx, SKY_T.top + 3, 0.8);
  blit(XFRONT_C);
  blit(FRONT_NEAR);
}
function drawMist(t) {
  const m = mistLevel(t);
  drawFog(FOG_W, 94, t, 2, 0.2 + 0.06 * wave(t, 5));                                  // wisps at the waist, always
  if (m > 0.01) {                                                                      // the bank rises into the waist and lifts off
    const y = 88 + Math.round(12 * (1 - m));
    drawFog(FOG_K, y, t, 3, 0.95 * m, true);
    drawFog(FOG_W, y + 8, t, 1, 0.6 * m);
    halo(HL.fogGlow, T.cx, 114, m);
    halo(HL.towerCore, T.cx, 112, 0.6 * m);
  }
}
/* ================= Per-frame: the residential tower and its billboard ================= */
const BB_FLICK = [[46.2, 47.4], [188.6, 189.4]];    // a spotlight stutters now and then
function drawResidence(t, billboard, pitch) {
  blit(RES_C);
  for (const w of RES_WINS) if (winLit(w, t)) { R(w.x, w.y, w.w, w.h, w.c1); if (w.w > 2) R(w.x + 1, w.y, 1, w.h, mixHex(w.c1, '#1a1420', 0.55)); }
  if (!billboard) return;
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.bbGlow, BB.x + 23, BB.y + 9, 0.9);
  blit(FRAME_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(ADS[step(t, 120) % 3], BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const [ax, on] of [[BB.x + 11, !fl], [BB.x + 36, true]]) { if (!on) continue; dot(ax, BB.y - 5, '#fff4d8'); halo(HL.spot, ax, BB.y - 3, 0.75); }
}
/* ================= Per-frame: the slope, the lookout, its wet boards ================= */
const PUD = [{ cx: 198, cy: 226, rx: 24, ry: 5 }, { cx: 290, cy: 238, rx: 18, ry: 4 }, { cx: 206, cy: 252, rx: 26, ry: 5 }];
const DECK_GL = [];                                 // wet boards catching the lamps and the sky
function drawCanopy(t) {
  ctx.drawImage(CANOPY_C, 0, 0);
  drawFog(FOG_L, 178, t, 2, 0.22 + 0.1 * wave(t, 6, 0.4), true);                    // low mist over the slope
}
function drawLookout(t) {
  const [sc, sx] = SCRATCH;
  sx.globalCompositeOperation = 'copy'; sx.drawImage(ctx.canvas, 0, 40, W, 60, 0, 0, W, 60); sx.globalCompositeOperation = 'source-over';
  ctx.drawImage(LOOK_C, 0, 0);
  for (const P of PUD) for (let y = P.cy - P.ry; y <= P.cy + P.ry; y++) {             // puddles on the boards mirror the sky and 101
    const hw = Math.round(P.rx * Math.sqrt(Math.max(0, 1 - ((y - P.cy) / (P.ry + 0.5)) ** 2)));
    if (hw < 1) continue;
    const src = 304 - y - 40, off = Math.round(Math.sin(TAU * 40 * t / LOOP + y * 0.9) * 0.8 + Math.sin(TAU * 97 * t / LOOP - y * 1.7) * 0.6);
    ctx.globalAlpha = 0.7; R(P.cx - hw, y, hw * 2, 1, '#07070f');
    if (src >= 0 && src < 60) { ctx.globalAlpha = 0.62; ctx.drawImage(sc, P.cx - hw - off, src, hw * 2, 1, P.cx - hw, y, hw * 2, 1); }
    ctx.globalAlpha = 1;
  }
  LAMPS.slice(0, 2).forEach((L, i) => streak(ctx, t, L.x, 4, '#ffd08a', 0.32, 3 + i * 5, L.foot + 2, Math.min(H, L.foot + 46)));
  streak(ctx, t, T.cx, 6, '#7ab8e0', 0.1, 9, DECK + 2, 222);
  for (const g of GLINTS) if (frac(g.k * t / LOOP * 4 + g.ph) < 0.09) dot(g.x, g.y, g.c);
  for (const g of DECK_GL) if (frac(g.k * t / LOOP * 3 + g.ph) < 0.1) dot(g.x, g.y, g.c);
  PUD.forEach((P, i) => {                                                              // drips from the trees ring the puddles
    for (let j = 0; j < 2; j++) {
      const { cyc, u } = cycle(t, 26 + i * 4 + j * 6, i * 0.37 + j * 0.5), rr = 1 + u * 5;
      const cx = P.cx + (hash(i, cyc, 61 + j) - 0.5) * P.rx * 1.2, cy = P.cy + (hash(i, cyc, 62 + j) - 0.5) * P.ry;
      ctx.globalAlpha = 0.55 * (1 - u);
      for (let a = 0; a < 12; a++) dot(cx + Math.cos(a / 12 * TAU) * rr, cy + Math.sin(a / 12 * TAU) * rr * 0.35, '#b8c4dc');
      ctx.globalAlpha = 1;
    }
  });
}

/* ================= Per-frame: the people on the lookout ================= */
const showOn = t => t >= SHOW.climb - 1 && t < SHOW.b;
const walkF = t => Math.floor(t * 3.2) & 1;          // stride frame (3.2 a second: a whole number a loop)
const groundY = (b, x) => Math.round(boulderTop(BOULDERS[b], x));
function phoneGlow(x, y) { R(x, y, 1, 2, '#9ad0ff'); halo(HL.screen, x, y + 1, 0.8); }
function drawCouple(t, umb) {
  const P = PEOPLE, sy = groundY(0, COUPLE.she), hy = COUPLE.heY, pose = Math.floor(frac(t / 12) * 3);
  if (showOn(t)) {                                                                     // both turn to the tower for the show
    person(COUPLE.she, sy, P.she, { view: 0, aR: 4 });
    person(COUPLE.he, hy, P.he, { view: 2, dir: 1, aR: 2 }); phoneGlow(COUPLE.he + 4, tyOf(P.he, hy) - 2);
  } else if (umb) {
    person(COUPLE.she, sy, P.she, { view: 0, umb: UMBS[5], umbR: 5 });
    person(COUPLE.he, hy, P.he, { view: 2, dir: -1, umb: UMBS[0], umbR: 6 });
  } else {
    person(COUPLE.she, sy, P.she, pose === 0 ? { view: 1, aL: 1, aR: 1 } : pose === 1 ? { view: 1, aR: 6 } : { view: 0, aR: 4 });
    if (pose < 2) { person(COUPLE.he, hy, P.he, { view: 2, dir: -1, aR: 2 }); phoneGlow(COUPLE.he - 4, tyOf(P.he, hy) - 2); }
    else person(COUPLE.he, hy, P.he, { view: 2, dir: -1, aR: 3, bend: true });       // checks the shots
  }
}
function drawSipper(t, umb) {
  const x = SIPPER.x, fy = BENCH.seat - 6, P = PEOPLE.sipper, sip = frac(t / 20 + 0.35) < 0.16;
  const hy = person(x, fy, P, { view: 0, sit: true, aR: sip ? 2 : 0, umb: umb ? UMBS[2] : null, umbR: 6 });
  if (sip) { R(x + 3, hy + 3, 2, 3, '#e8d4b4'); R(x + 3, hy + 5, 2, 1, '#3a2014'); R(x + 4, hy + 1, 1, 2, '#ff6ab8'); }   // a tall cup of bubble tea
}
function drawPhoto(t, umb) {
  const fy = DECK + 3, x = PHOTO.x, tx = PHOTO.tri, P = PEOPLE.photo, u = showOn(t) ? 0.3 : frac(t / 30 + 0.1);
  ctx.fillStyle = '#26242e';
  for (const lx of [tx - 4, tx, tx + 3]) { const n = fy - 189; for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(tx, lx, k / n)), 189 + k, 1, 1); }
  R(tx - 1, 186, 4, 3, '#14141a'); R(tx - 2, 187, 1, 1, '#3a3a46'); R(tx, 186, 2, 1, '#3a3a46');
  const o = { umb: umb ? UMBS[4] : null, umbR: 7, umbX: -4 };
  if (u < 0.6) {                                                                       // a long exposure: bent to the viewfinder
    if (frac(t) < 0.5) { dot(tx + 2, 188, '#ff3030'); halo(HL.red, tx + 2, 188, 0.5); }
    person(x, fy, P, { ...o, view: 2, dir: -1, bend: true, aR: 2 });
  } else if (u < 0.85) {
    person(x, fy, P, { ...o, view: 2, dir: -1, aR: 3 });
    const ty = tyOf(P, fy); R(x - 5, ty + 1, 2, 2, '#9ad0ff'); halo(HL.screen, x - 4, ty + 2, 0.9);
  } else person(x, fy, P, { ...o, view: 0 });
}
function drawFriends(t, umb) {
  const P = PEOPLE, fys = [DECK + 2, DECK + 3, DECK + 2], selfie = inWin(t, 74, 82), plane = inWin(t, 30, 40) || inWin(t, 150, 158);
  const L = [P.fA, P.fB, P.fC];
  FRIENDS.forEach((x, i) => {
    let o = { view: 0 };
    if (showOn(t)) o = i === 1 ? { view: 0, aR: 2 } : { view: 0, aR: i === 2 ? 4 : 0 };
    else if (selfie) o = { view: 1, aR: i === 1 ? 1 : i === 0 ? 5 : 0, aL: i === 2 ? 5 : 0, lean: i === 0 ? 1 : i === 2 ? -1 : 0 };
    else if (plane && i === 0) o = { view: 0, aL: 4 };
    else if (frac(t / 40 + i * 0.3) < 0.14) o = { view: 2, dir: i === 2 ? -1 : 1 };  // turn to talk
    if (umb && i !== 1) o = { ...o, umb: i === 0 ? UMBS[3] : null, umbR: 9, umbX: 4 };
    person(x, fys[i], L[i], o);
    if (showOn(t) && i === 1) phoneGlow(x + 1, tyOf(L[i], fys[i]) - 3);
    if (selfie && i === 1) R(x + 3, tyOf(L[i], fys[i]) - 10, 2, 3, '#22222c');         // the phone held up, its back to us
  });
}
function drawFamily(t, umb) {
  const x = FAMILY.x, fy = DECK + 3, P = PEOPLE, hy = person(x, fy, P.parent, { view: 0, aL: 1, aR: 1, umb: umb ? UMBS[1] : null, umbR: 8 });
  const wave2 = showOn(t) || inWin(t, 32, 42) || inWin(t, 152, 160);
  person(x, hy + 3, P.child, { view: 0, sit: true, aR: wave2 ? (frac(t / 2) < 0.5 ? 1 : 4) : 0, aL: 0 });   // on the parent's shoulders
}
function drawSitters(t, umb) {
  const [xa, xb] = SITTERS, lean = frac(t / 40 + 0.5) < 0.18, drink = frac(t / 24) < 0.14;
  person(xa, groundY(1, xa), PEOPLE.sitA, { view: 0, sit: true, lean: lean ? 1 : 0, umb: umb ? UMBS[5] : null, umbR: 9, umbX: 5 });
  person(xb, groundY(1, xb), PEOPLE.sitB, { view: 0, sit: true, lean: lean ? -1 : 0, aR: drink ? 2 : 0 });
}
function drawSelfie(t) {
  const x = SELFIE.x, fy = DECK + 4, P = PEOPLE.selfie, u = frac(t / 16);
  if (u < 0.55) {
    person(x, fy, P, { view: 1, aR: 4 });
    const ty = tyOf(P, fy); ctx.fillStyle = '#4a4a56';
    for (let k = 0; k < 7; k++) ctx.fillRect(x + 7 + k, ty - 4 - k, 1, 1);             // the selfie stick
    R(x + 13, ty - 13, 2, 3, '#1c1c24');
  } else { person(x, fy, P, { view: 0, aR: 2 }); phoneGlow(x + 1, tyOf(P, fy) - 3); }  // a photo of the tower
}
function drawLone(t) {
  const x = LONE.x, fy = groundY(2, x), drink = frac(t / 30 + 0.7) < 0.12;
  const hy = person(x, fy, PEOPLE.lone, { view: 1, sit: true, aR: drink ? 2 : 3 });
  if (drink) R(x + 3, hy + 2, 1, 3, '#a8c8e8'); else R(x + 6, fy - 4, 1, 3, '#a8c8e8');  // a water bottle
}
/** the climb: feet positions up the steps (bottom first) and along the boards to x */
function hikePath(x) {
  const pts = [[420, 286]];
  for (let k = STEPS.length - 1; k >= 0; k--) pts.push([STEPS[k].x0 + 12, STEPS[k].y + 1]);
  pts.push([334, DECK + 3], [x, DECK + 3]);
  let len = 0;
  const cum = [0];
  for (let i = 1; i < pts.length; i++) { len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); cum.push(len); }
  return { pts, cum, len };
}
const HIKE_PATHS = HIKE.map(h => hikePath(h.x));
function pathAt(P, f) {
  const d = clamp(f, 0, 1) * P.len;
  for (let i = 1; i < P.pts.length; i++) if (d <= P.cum[i]) {
    const u = (d - P.cum[i - 1]) / (P.cum[i] - P.cum[i - 1] || 1), a = P.pts[i - 1], b = P.pts[i];
    return { x: lerp(a[0], b[0], u), y: lerp(a[1], b[1], u), dir: b[0] < a[0] ? -1 : 1 };
  }
  const e = P.pts[P.pts.length - 1];
  return { x: e[0], y: e[1], dir: 1 };
}
function drawHikers(h, idx, t, umb) {
  if (t < h.t0 || t >= h.t1) return;
  const Pth = HIKE_PATHS[idx], [A, B] = h.seed === 1 ? [PEOPLE.hA, PEOPLE.hB] : [PEOPLE.hC, PEOPLE.hD];
  for (let j = 0; j < 2; j++) {
    const lag = j * 2.2, tt = t - lag;
    let f, moving = true, back = false;
    if (tt < h.at) f = (tt - h.t0) / (h.at - h.t0);
    else if (tt < h.go) { f = 1; moving = false; }
    else { f = 1 - (tt - h.go) / (h.t1 - 2.2 - h.go); back = true; }
    if (f < 0) continue;
    const q = pathAt(Pth, f), x = q.x + (j ? 9 : 0) * (moving ? 0 : 1), P = j ? B : A;
    if (!moving) {
      const point = (h.seed === 1 && inWin(t, 36, 44)) || (h.seed === 2 && inWin(t, 160, 166)) || showOn(t);
      person(x, q.y, P, { view: 0, aR: j === 0 && point ? 4 : 0, aL: j === 1 && point ? 2 : 0, umb: umb ? UMBS[j ? 1 : 3] : null, umbR: 7 });
    } else person(x, q.y, P, { view: 2, dir: back ? -q.dir : q.dir, step: walkF(t + j * 0.15), umb: umb ? UMBS[j ? 1 : 3] : null, umbR: 7 });
  }
}
function drawDogWalker(t) {
  for (const D of DOGW) {
    if (t < D.t0 || t >= D.t1) continue;
    const d = D.dir, mid = d > 0 ? 208 : 236, fy = 224;
    const s = along([[D.t0, d > 0 ? -30 : W + 30], [D.t0 + 11, mid], [D.t0 + 17, mid], [D.t1, d > 0 ? W + 44 : -44]], t);
    const x = Math.round(s.x), dx = x + d * 15, df = s.moving ? (Math.floor(x / 3) & 1) : 2;
    ctx.drawImage(DOG_SPR[df][d > 0 ? 0 : 1], dx - 6, fy - 6);
    ctx.fillStyle = 'rgba(8,8,20,0.35)'; ctx.fillRect(dx - 4, fy + 1, 9, 1);
    person(x, fy, PEOPLE.dogw, { view: 2, dir: d, step: s.moving && (Math.floor(x / 4) & 1), aR: 3 });
    const hx = x + d * 4, hy = tyOf(PEOPLE.dogw, fy) + 2, cx = dx + d * 3, cy = fy - 4, n = Math.abs(cx - hx);
    ctx.fillStyle = '#c8323a';
    for (let k = 1; k < n; k++) ctx.fillRect(hx + Math.sign(cx - hx) * k, Math.round(lerp(hy, cy, k / n) + Math.sin(Math.PI * k / n) * 2), 1, 1);
  }
}
function drawJogger(t) {
  for (const J of JOG) {
    if (t < J.t0 || t >= J.t1) continue;
    const u = (t - J.t0) / (J.t1 - J.t0), d = J.dir, x = Math.round(d > 0 ? lerp(-20, W + 20, u) : lerp(W + 20, -20, u)), f = Math.floor(x / 5) & 1;
    person(x, 238 - f, PEOPLE.jog, { view: 2, dir: d, step: f, aR: 4 });
  }
}
function drawCat(t) {
  const fy = groundY(1, CAT.x) + 1, groom = frac(t / 7.5 + 0.2) < 0.3;
  ctx.drawImage(CAT_SPR[groom ? 1 : 0], CAT.x, fy - 8);
  if (frac(t / 3.75) < 0.5) dot(CAT.x + 7, fy - 2, '#6a5440'); else dot(CAT.x + 6, fy - 2, '#6a5440');   // the tail flicks
  ctx.fillStyle = 'rgba(8,8,20,0.35)'; ctx.fillRect(CAT.x, fy, 8, 1);
}
function drawPeople(t, crowd, wet) {
  const DL = [], put = (fy, fn) => DL.push([fy, fn]), umb = wet && inWin(t, UMB[0], UMB[1]);
  put(groundY(1, CAT.x) + 1, () => drawCat(t));
  if (here(RANK.couple, crowd)) { put(groundY(0, COUPLE.she), () => drawCouple(t, umb)); }
  put(BENCH.seat + 7, () => { if (here(RANK.sipper, crowd)) drawSipper(t, umb); ctx.drawImage(BENCH_C, BENCH.x0, BENCH.seat - 8); });
  if (here(RANK.photo, crowd)) put(DECK + 3, () => drawPhoto(t, umb));
  if (here(RANK.friends, crowd)) put(DECK + 2.5, () => drawFriends(t, umb));
  if (here(RANK.family, crowd)) put(DECK + 3, () => drawFamily(t, umb));
  if (here(RANK.sitters, crowd)) put(groundY(1, SITTERS[0]), () => drawSitters(t, umb));
  if (here(RANK.selfie, crowd)) put(DECK + 4, () => drawSelfie(t));
  if (here(RANK.lone, crowd)) put(groundY(2, LONE.x), () => drawLone(t));
  HIKE.forEach((h, i) => { if (here(i ? RANK.hikeB : RANK.hikeA, crowd) && t >= h.t0 && t < h.t1) put(t < h.at - 2 || t > h.go + 2 ? 240 : DECK + 3, () => drawHikers(h, i, t, umb)); });
  if (here(RANK.dog, crowd)) put(224, () => drawDogWalker(t));
  if (here(RANK.jog, crowd)) put(238, () => drawJogger(t));
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
}
function drawLamps(t) {
  for (const L of LAMPS) {
    const I = 0.93 + 0.04 * wave(t, 600, L.x * 0.01);
    halo(HL.lamp, L.x, L.y, I); halo(HL.lampCore, L.x, L.y, I);
    for (let m = 0; m < 3; m++) dot(L.x + Math.round((5 + m) * wave(t, 42 + m * 12, m / 3)), L.y - 1 + Math.round(4 * wave(t, 31 + m * 10, m / 5)), '#e8e0c8');   // moths
  }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the rooftop billboard; billboard: false leaves the sign out (the
// loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own drizzle when left out; crowd: the
// share of the people to show, 0..1 (CROWD when left out or not a number), 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  const full = weather === 'drizzle', wet = (!!wx.drizzle && (full || showerOn(t) > 0.3)) || ['rain', 'thunderstorm'].includes(wx.shared);
  drawSky(t);
  drawFar(t);
  drawPlanes(t);
  drawMid(t);
  if (wx.drizzle) drawDrizzle(DRIZ[0], t, lights, full);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawTower(t);
  drawFront(t);
  drawMist(t);
  drawResidence(t, billboard, pitch);
  drawCanopy(t);
  if (wx.drizzle) drawDrizzle(DRIZ[1], t, lights, full);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawLookout(t);
  drawPeople(t, crowd, wet);
  drawLamps(t);
  if (wx.drizzle) drawDrizzle(DRIZ[2], t, lights, full);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'taipei',
  name: 'Taipei',
  tagline: 'Mist round Taipei 101',
  timeZone: 'Asia/Taipei',
  accent: '#e040fb',                                   // written out: tools/seo/seo.mjs reads it from the source
  focusX: T.cx,
  landmark: [182, 211],                                // Taipei 101, pedestal to pedestal, its halo's core
  landmarkTop: T.tip - 1,                              // the beacon on the spire's tip
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'landmark', x: 182, y: 13, w: 30, h: 146 },
    { kind: 'window', x: 115, y: 163, w: 5, h: 4 },   // (the cat sits below the rows a 21:9 screen keeps, so it is no spot)
  ],
  ambience: { crickets: 0.38, traffic: 0.24, wind: 0.14, rain: 0.16 },
  events: [
    ...TRAINS.map(tr => ({ t: tr.t0 + 1, type: 'train', dur: 20, pan: tr.dir > 0 ? [-1, 0.4] : [0.4, -1], gain: 0.08 })),
    { t: SHOW.climb, type: 'chime', x: T.cx, gain: 0.3 },
    { t: SHOW.top, type: 'chime', x: T.cx, gain: 0.22 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
